/**
 * DataTable — Farmaster Production Table Component
 *
 * Features:
 * - TanStack Table v9 (sorting, filtering, column pinning, column reordering);
 *   zestaw funkcji, typy kolumn i zgodnosc z v8 w `data-table-features.ts`
 * - TanStack Virtual v3 (row virtualization — activates at >100 rows by default)
 * - @dnd-kit drag-and-drop column reordering
 * - Inline per-column filters (text + numeric operators: >, >=, <, <=, =)
 * - Sticky header and summary inside one native scroll container
 * - Column pinning with accurate DOM-measured offsets + shadow separator
 * - Column order persistence via localStorage (per tableId)
 * - Column pinning persistence via localStorage (per tableId)
 * - Column visibility (show/hide columns) with persistence
 * - Column Schemes: save/load named layout snapshots (order + pinning + visibility)
 * - Grouping: client-side pre-aggregation by selected text columns
 * - Aggregation options: exclude negatives, average, average-without-zero
 * - Filter reset via filterResetKey prop or onClearFiltersReady callback
 * - Farmaster design tokens throughout — NO hardcoded colors
 *
 * Virtualization: position:absolute + translateY div-based rows (no spacer <tr>)
 *
 * Ported from: concept/farmaster-project-2/components/ui/data-table.tsx
 * Refactored: 2026-02-17 — table -> div virtualization
 * Extended:   2026-02-18 — toolbar (schemes, column toggle, grouping, aggregation)
 */

import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import {
	arrayMove,
	horizontalListSortingStrategy,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
	type ColumnFiltersState,
	type ColumnOrderState,
	type ColumnSizingState,
	flexRender,
	type SortingState,
	useTable,
	// v9 nazywa ten typ ColumnVisibilityState; alias zachowuje publiczny
	// eksport `VisibilityState` z tego modulu.
	type ColumnVisibilityState as VisibilityState,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
	ArrowDown,
	ArrowUp,
	ArrowUpDown,
	Check,
	ChevronDown,
	ChevronRight,
	Columns3,
	Crosshair,
	Download,
	Filter as FilterIcon,
	GripVertical,
	Group,
	HelpCircle,
	Layers,
	Loader2,
	Palette,
	Pin,
	PinOff,
	Save,
	Sigma,
	Trash2,
} from "lucide-react";
import * as React from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { useDebouncedCallback } from "use-debounce";
import { BottomSheet, SheetFooterActions } from "@/components/ui/bottom-sheet";
import {
	type Column,
	type ColumnDef,
	type ColumnPinningState,
	type DataTableValueRow,
	dataTableFeatures,
	legacyAutoSortFn,
	normalizeColumnPinning,
	type Row,
	type RowData,
} from "@/components/ui/data-table-features";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
	MobileListView,
	MobileRowDetails,
	MobileSortSheet,
	TableModeToggle,
	useTableMode,
} from "@/components/ui/mobile-list";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
	fallbackTableId,
	resolveColumnRoles,
	type TableMobileMode,
} from "@/components/ui/table-mobile";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useIsBelowMd } from "@/hooks/use-mobile";
import { heatmapRgb } from "@/lib/colors";
import { beginDataExport } from "@/lib/data-export-events";
import { attemptDynamicImportRecovery } from "@/lib/dynamic-import";
import {
	matchesNumericFilter,
	type NumericFilterValue,
	opToSymbol,
	parseNumericFilter,
	symbolToOp,
} from "@/lib/numeric-filter";
import { tableBreadcrumb } from "@/lib/sentry";
import { cn } from "@/lib/utils";

// `columnDef.meta` (isNumeric, isDate, facetedOptions, filterRender, ...) jest
// typowane slotem `columnMeta` w `dataTableFeatures` — patrz DataTableColumnMeta.

// Kolumna o id "actions" (przycisk akcji) jest zawsze ostatnia — konwencja
// repo-wide, bez dopisywania meta.alwaysLast na kazdej stronie. Flaga meta
// zostaje dla kolumn o innych id (np. rozbicia w daily-sales).
export function isAlwaysLastColumn(column: {
	id: string;
	columnDef: { meta?: { alwaysLast?: boolean } };
}): boolean {
	return column.columnDef.meta?.alwaysLast === true || column.id === "actions";
}

/* ============================================================================
   Aggregation Types & Helpers
   ============================================================================ */

export interface AggregationConfig {
	/** Exclude negative values from sum/average calculation */
	excludeNegatives: boolean;
	/** Show average instead of sum in the summary row */
	useAverage: boolean;
	/** When averaging: skip zero values */
	averageExcludeZero: boolean;
	useMedian: boolean;
	/** When computing median: skip zero values */
	medianExcludeZero: boolean;
}

const DEFAULT_AGG_CONFIG: AggregationConfig = {
	excludeNegatives: false,
	useAverage: false,
	averageExcludeZero: false,
	useMedian: false,
	medianExcludeZero: false,
};

export interface TableScheme {
	id: string;
	name: string;
	columnOrder: ColumnOrderState;
	columnPinning: ColumnPinningState;
	columnVisibility: VisibilityState;
	columnSizing?: ColumnSizingState;
	groupBy?: string[];
}

/** Compute a single aggregated number from a list of values + config */
function computeMedian(values: number[]): number {
	if (values.length === 0) return 0;
	const sorted = [...values].sort((a, b) => a - b);
	const mid = Math.floor(sorted.length / 2);
	return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

function computeAggValue(values: number[], config: AggregationConfig): number {
	const filtered = config.excludeNegatives ? values.filter((v) => v >= 0) : values;
	if (config.useMedian) {
		const pool = config.medianExcludeZero ? filtered.filter((v) => v !== 0) : filtered;
		return computeMedian(pool);
	}
	if (config.useAverage) {
		const pool = config.averageExcludeZero ? filtered.filter((v) => v !== 0) : filtered;
		return pool.length > 0 ? pool.reduce((s, v) => s + v, 0) / pool.length : 0;
	}
	return filtered.reduce((s, v) => s + v, 0);
}

/**
 * Coerce a cell value to number for aggregation. Backend uses rust_decimal which
 * serializes to strings — without this helper they'd be filtered out and group
 * sums collapse to 0. Number-typed values pass through untouched. Returns null
 * for non-numeric/empty so callers can `.filter(v => v !== null)` cleanly.
 */
function toNumeric(v: unknown): number | null {
	if (typeof v === "number") return Number.isNaN(v) ? null : v;
	if (typeof v === "string" && v !== "") {
		const n = Number(v);
		return Number.isFinite(n) ? n : null;
	}
	return null;
}

/* ============================================================================
   Toolbar Primitives
   ============================================================================ */

/**
 * Telefon (<md): pasek narzedzi tabeli jest rzedem ikon 36 px (schematy,
 * kolumny, eksport), a naglowek kolumny w trybie „Tabela” jest samym tytulem
 * otwierajacym menu. Desktop dostaje wartosci domyslne — bez zmian.
 */
const DataTableMobileContext = React.createContext<{
	compactToolbar: boolean;
	mobileHeader: boolean;
}>({ compactToolbar: false, mobileHeader: false });

/**
 * Arkusze filtrow na telefonie odmontowuja pole od razu po „Pokaż …”, a pole
 * zapisuje filtr z opoznieniem (debounce) — bez rejestru szybkie stukniecie
 * gubiloby wpisana wartosc. Arkusz zatwierdza (`commit`) albo odrzuca
 * (`discard`, przy „Wyczyść”) oczekujace zapisy przed zamknieciem.
 */
interface PendingFilterWrite {
	commit: () => void;
	discard: () => void;
}
const PendingFilterWritesContext = React.createContext<Set<PendingFilterWrite> | null>(null);

function flushPendingFilterWrites(
	writes: Set<PendingFilterWrite>,
	action: keyof PendingFilterWrite,
) {
	for (const write of [...writes]) write[action]();
}

function useOutsideClick(callback: () => void) {
	const ref = React.useRef<HTMLDivElement>(null);
	const callbackRef = React.useRef(callback);
	callbackRef.current = callback;

	React.useEffect(() => {
		function handler(e: MouseEvent) {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				callbackRef.current();
			}
		}
		document.addEventListener("mousedown", handler);
		return () => document.removeEventListener("mousedown", handler);
	}, []);

	return ref;
}

const DEFAULT_COLUMN_COLOR = `#${heatmapRgb.light
	.map((value) => value.toString(16).padStart(2, "0"))
	.join("")}`;
type ColumnColors = Record<string, string>;

function isColumnColor(value: unknown): value is string {
	return typeof value === "string" && /^#[0-9a-f]{6}$/i.test(value);
}

function normalizeColumnColor(value: unknown): string {
	return isColumnColor(value) ? value : DEFAULT_COLUMN_COLOR;
}

function parseColumnColors(value: unknown): ColumnColors | null {
	if (!value || typeof value !== "object" || Array.isArray(value)) return null;
	return Object.fromEntries(Object.entries(value).filter(([, color]) => isColumnColor(color)));
}

function hexToRgb(color: string): readonly [number, number, number] {
	const value = Number.parseInt(color.slice(1), 16);
	return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function buildColumnColorStyle(color: string): React.CSSProperties {
	const [r, g, b] = hexToRgb(color);
	return {
		background: `rgba(${r},${g},${b},0.15)`,
		color: "var(--card-foreground)",
		"--color-primary": "var(--card-foreground)",
		"--color-foreground": "var(--card-foreground)",
		"--color-muted-foreground": "var(--card-foreground)",
	} as React.CSSProperties;
}

export function ToolbarButton({
	icon,
	label,
	onClick,
	active = false,
	hasChevron = false,
	className,
	ariaExpanded,
	ariaControls,
	disabled = false,
}: {
	icon?: React.ReactNode;
	label: string;
	onClick: () => void;
	active?: boolean;
	hasChevron?: boolean;
	className?: string;
	ariaExpanded?: boolean;
	ariaControls?: string;
	disabled?: boolean;
}) {
	// Ikona bez podpisu tylko na telefonie i tylko gdy jest ikona — przycisk
	// bez ikony musi zachowac tekst, inaczej bylby pustym kwadratem.
	const onPhone = React.useContext(DataTableMobileContext).compactToolbar;
	const compact = onPhone && !!icon;
	return (
		<button
			type="button"
			onClick={onClick}
			disabled={disabled}
			aria-expanded={ariaExpanded}
			aria-haspopup={hasChevron ? "true" : undefined}
			aria-controls={ariaControls}
			aria-label={compact ? label : undefined}
			title={compact ? label : undefined}
			className={cn(
				"flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium transition-colors select-none disabled:pointer-events-none disabled:opacity-50",
				active
					? "bg-primary/10 text-primary"
					: "text-muted-foreground hover:bg-muted hover:text-foreground",
				compact && "size-9 justify-center px-0 py-0 [&_svg]:size-4",
				!compact && onPhone && "min-h-9",
				className,
			)}
		>
			{icon}
			{compact ? null : label}
			{hasChevron && !compact && <ChevronDown className="size-3 opacity-60" />}
		</button>
	);
}

function DropdownPanel({
	id,
	open,
	anchorRef,
	children,
	minWidth = 180,
	align = "left",
}: {
	id?: string;
	open: boolean;
	anchorRef: React.RefObject<HTMLDivElement | null>;
	children: React.ReactNode;
	minWidth?: number;
	align?: "left" | "right";
}) {
	const [style, setStyle] = React.useState<React.CSSProperties>({});

	React.useEffect(() => {
		if (!open) return;

		const reposition = () => {
			const el = anchorRef.current;
			if (!el) return;
			const rect = el.getBoundingClientRect();
			const modalRect = el
				.closest('[role="dialog"], [role="alertdialog"]')
				?.getBoundingClientRect();
			const position = modalRect ? "absolute" : "fixed";
			const gapPx = 4;
			const bottom = (modalRect?.bottom ?? window.innerHeight) - rect.top + gapPx;
			const width = Math.max(minWidth, rect.width);
			// Panel nie moze wyjechac za prawa krawedz waskiego ekranu (telefon).
			const maxLeft = Math.max(8, window.innerWidth - width - 8);
			setStyle(
				align === "right"
					? {
							position,
							bottom,
							right: (modalRect?.right ?? window.innerWidth) - rect.right,
							minWidth: width,
							maxWidth: "calc(100vw - 16px)",
							zIndex: 9999,
						}
					: {
							position,
							bottom,
							left: Math.min(rect.left, maxLeft) - (modalRect?.left ?? 0),
							minWidth: width,
							maxWidth: "calc(100vw - 16px)",
							zIndex: 9999,
						},
			);
		};

		reposition();
		window.addEventListener("scroll", reposition, true);
		window.addEventListener("resize", reposition);
		return () => {
			window.removeEventListener("scroll", reposition, true);
			window.removeEventListener("resize", reposition);
		};
	}, [open, align, minWidth, anchorRef]);

	if (!open || typeof document === "undefined") return null;

	return createPortal(
		// biome-ignore lint/a11y/noStaticElementInteractions: stopPropagation prevents portal clicks from reaching document-level outside-click handler
		<div
			id={id}
			className="rounded-md border border-border bg-card shadow-lg max-md:max-h-[70dvh] max-md:overflow-y-auto max-md:text-sm max-md:[&_button]:min-h-9 max-md:[&_label]:min-h-9"
			style={style}
			onMouseDown={(e) => e.stopPropagation()}
		>
			{children}
		</div>,
		// Keep toolbar menus inside the modal's pointer and focus boundary.
		anchorRef.current?.closest('[role="dialog"], [role="alertdialog"]') ?? document.body,
	);
}

function ToolbarCheckbox({
	checked,
	onChange,
	label,
}: {
	checked: boolean;
	onChange: (v: boolean) => void;
	label: string;
}) {
	return (
		<label className="flex cursor-pointer select-none items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground transition-colors">
			<input
				type="checkbox"
				checked={checked}
				onChange={(e) => onChange(e.target.checked)}
				className="size-3 cursor-pointer accent-primary"
			/>
			{label}
		</label>
	);
}

/* ============================================================================
   Scheme Dropdown
   ============================================================================ */

interface SchemeDropdownProps {
	schemes: TableScheme[];
	activeSchemeId: string | null;
	onApply: (scheme: TableScheme) => void;
	onSave: (name: string) => void;
	onDelete: (id: string) => void;
	onApplyDefault: () => void;
}

function SchemeDropdown({
	schemes,
	activeSchemeId,
	onApply,
	onSave,
	onDelete,
	onApplyDefault,
}: SchemeDropdownProps) {
	const [open, setOpen] = React.useState(false);
	const [saveName, setSaveName] = React.useState("");
	const panelId = React.useId();
	const containerRef = useOutsideClick(React.useCallback(() => setOpen(false), []));

	const handleSave = () => {
		const name = saveName.trim();
		if (!name) return;
		onSave(name);
		setSaveName("");
		setOpen(false);
	};

	const isDefault = activeSchemeId === null || activeSchemeId === "default";

	return (
		<div ref={containerRef} className="relative">
			<ToolbarButton
				icon={<Save className="size-3" />}
				label="Schematy"
				onClick={() => setOpen((o) => !o)}
				active={!isDefault}
				hasChevron
				ariaExpanded={open}
				ariaControls={panelId}
			/>
			<DropdownPanel id={panelId} open={open} anchorRef={containerRef} minWidth={230}>
				<div className="p-1.5">
					<button
						type="button"
						onClick={() => {
							onApplyDefault();
							setOpen(false);
						}}
						className={cn(
							"flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs transition-colors hover:bg-muted",
							isDefault && "font-semibold text-primary",
						)}
					>
						<Layers className="size-3 shrink-0 text-muted-foreground" />
						Domyslny
						{isDefault && <span className="ml-auto text-[10px] text-primary/70">aktywny</span>}
					</button>

					{schemes.length > 0 && <div className="my-1 border-t border-border" />}

					{schemes.map((scheme) => {
						const isActive = activeSchemeId === scheme.id;
						return (
							<div
								key={scheme.id}
								className={cn(
									"group flex items-center gap-1 rounded px-2 py-1.5 text-xs transition-colors hover:bg-muted",
									isActive && "font-semibold text-primary",
								)}
							>
								<button
									type="button"
									className="min-w-0 flex-1 truncate text-left"
									onClick={() => {
										onApply(scheme);
										setOpen(false);
									}}
								>
									{scheme.name}
								</button>
								{isActive && <span className="shrink-0 text-[10px] text-primary/70">aktywny</span>}
								<button
									type="button"
									onClick={() => onDelete(scheme.id)}
									className="ml-1 shrink-0 rounded p-0.5 text-muted-foreground/40 opacity-0 transition-all hover:text-destructive group-hover:opacity-100"
									title="Usun schemat"
								>
									<Trash2 className="size-3" />
								</button>
							</div>
						);
					})}

					<div className="mt-1.5 border-t border-border pt-1.5">
						<div className="flex gap-1 px-0.5">
							<input
								type="text"
								value={saveName}
								onChange={(e) => setSaveName(e.target.value)}
								placeholder="Nazwa schematu..."
								className="min-w-0 flex-1 rounded border border-border bg-background px-2 py-1 text-xs text-foreground placeholder:text-muted-foreground/50 focus:border-ring focus:outline-none"
								onKeyDown={(e) => {
									if (e.key === "Enter") handleSave();
									if (e.key === "Escape") setOpen(false);
								}}
							/>
							<button
								type="button"
								onClick={handleSave}
								disabled={!saveName.trim()}
								className="shrink-0 rounded bg-primary px-2 py-1 text-[10px] font-medium text-primary-foreground transition-opacity disabled:opacity-40"
							>
								Zapisz
							</button>
						</div>
					</div>
				</div>
			</DropdownPanel>
		</div>
	);
}

/* ============================================================================
   Column Toggle Dropdown
   ============================================================================ */

function ColumnToggleDropdown<TData extends RowData>({
	columns,
	columnLabels,
}: {
	columns: Column<TData, unknown>[];
	columnLabels: Record<string, string>;
}) {
	const [open, setOpen] = React.useState(false);
	const panelId = React.useId();
	const containerRef = useOutsideClick(React.useCallback(() => setOpen(false), []));

	const toggleable = columns.filter((c) => c.getCanHide());
	const hiddenCount = toggleable.filter((c) => !c.getIsVisible()).length;

	return (
		<div ref={containerRef} className="relative">
			<ToolbarButton
				icon={<Columns3 className="size-3" />}
				label={hiddenCount > 0 ? `Kolumny (${hiddenCount})` : "Kolumny"}
				onClick={() => setOpen((o) => !o)}
				active={hiddenCount > 0}
				hasChevron
				ariaExpanded={open}
				ariaControls={panelId}
			/>
			<DropdownPanel id={panelId} open={open} anchorRef={containerRef} minWidth={210}>
				<div className="p-1.5">
					<div className="mb-1.5 flex items-center justify-between px-1">
						<span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
							Widocznosc kolumn
						</span>
						{hiddenCount > 0 && (
							<button
								type="button"
								onClick={() => {
									tableBreadcrumb("columns.showAll", { wasHidden: hiddenCount });
									for (const c of toggleable) c.toggleVisibility(true);
								}}
								className="text-[10px] text-primary hover:underline"
							>
								Pokaz wszystkie
							</button>
						)}
					</div>
					<div className="max-h-72 overflow-y-auto">
						{toggleable.map((col) => {
							const toggleHandler = col.getToggleVisibilityHandler();
							return (
								<label
									key={col.id}
									className="flex cursor-pointer select-none items-center gap-2 rounded px-2 py-1.5 text-xs transition-colors hover:bg-muted"
								>
									<input
										type="checkbox"
										checked={col.getIsVisible()}
										onChange={(e) => {
											tableBreadcrumb("columns.toggle", {
												col: col.id,
												visible: e.target.checked,
											});
											toggleHandler(e);
										}}
										className="size-3 cursor-pointer accent-primary"
									/>
									<span
										className={col.getIsVisible() ? "text-foreground" : "text-muted-foreground"}
									>
										{columnLabels[col.id] ?? col.id}
									</span>
								</label>
							);
						})}
					</div>
				</div>
			</DropdownPanel>
		</div>
	);
}

/* ============================================================================
   Group-By Dropdown
   ============================================================================ */

function GroupByDropdown<TData extends RowData>({
	columns,
	columnLabels,
	activeGroupBy,
	onGroupByChange,
}: {
	columns: Column<TData, unknown>[];
	columnLabels: Record<string, string>;
	activeGroupBy: string[];
	onGroupByChange: (ids: string[]) => void;
}) {
	const [open, setOpen] = React.useState(false);
	const panelId = React.useId();
	const containerRef = useOutsideClick(React.useCallback(() => setOpen(false), []));

	const groupable = columns.filter((c) => !c.columnDef.meta?.isNumeric);

	function toggle(id: string) {
		const willBeActive = !activeGroupBy.includes(id);
		tableBreadcrumb("groupBy.toggle", {
			col: id,
			active: willBeActive,
			count: willBeActive ? activeGroupBy.length + 1 : activeGroupBy.length - 1,
		});
		onGroupByChange(
			activeGroupBy.includes(id) ? activeGroupBy.filter((x) => x !== id) : [...activeGroupBy, id],
		);
	}

	return (
		<div ref={containerRef} className="relative">
			<ToolbarButton
				icon={<Group className="size-3.5" />}
				label={activeGroupBy.length > 0 ? `Grupuj (${activeGroupBy.length})` : "Grupuj"}
				onClick={() => setOpen((o) => !o)}
				active={activeGroupBy.length > 0}
				hasChevron
				ariaExpanded={open}
				ariaControls={panelId}
			/>
			<DropdownPanel id={panelId} open={open} anchorRef={containerRef} minWidth={220}>
				<div className="p-1.5">
					<div className="mb-1.5 flex items-center justify-between px-1">
						<span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
							Grupuj po kolumnach
						</span>
						{activeGroupBy.length > 0 && (
							<button
								type="button"
								onClick={() => {
									tableBreadcrumb("groupBy.clear", { was: activeGroupBy.length });
									onGroupByChange([]);
								}}
								className="text-[10px] text-primary hover:underline"
							>
								Wyczysc
							</button>
						)}
					</div>
					<div className="max-h-64 overflow-y-auto">
						{groupable.map((col) => {
							const isActive = activeGroupBy.includes(col.id);
							const order = activeGroupBy.indexOf(col.id);
							// meta.groupable === false -> checkbox disabled (kolumna niewspierana dla GROUP BY,
							// np. server-side grouping akceptuje tylko okreslony set dimensji).
							const groupableFlag = (col.columnDef.meta as { groupable?: boolean } | undefined)
								?.groupable;
							const isDisabled = groupableFlag === false && !isActive;
							return (
								<label
									key={col.id}
									className={
										isDisabled
											? "flex select-none items-center gap-2 rounded px-2 py-1.5 text-xs opacity-50 cursor-not-allowed"
											: "flex cursor-pointer select-none items-center gap-2 rounded px-2 py-1.5 text-xs transition-colors hover:bg-muted"
									}
									title={isDisabled ? "Tej kolumny nie mozna uzyc do grupowania" : undefined}
								>
									<input
										type="checkbox"
										checked={isActive}
										disabled={isDisabled}
										onChange={() => {
											if (!isDisabled) toggle(col.id);
										}}
										className={
											isDisabled
												? "size-3 cursor-not-allowed accent-primary"
												: "size-3 cursor-pointer accent-primary"
										}
									/>
									<span
										className={isActive ? "font-medium text-foreground" : "text-muted-foreground"}
									>
										{columnLabels[col.id] ?? col.id}
									</span>
									{isActive && (
										<span className="ml-auto text-[10px] text-primary/70">#{order + 1}</span>
									)}
								</label>
							);
						})}
					</div>
					{activeGroupBy.length > 0 && (
						<div className="mt-1 border-t border-border px-2 pt-1.5">
							<p className="text-[10px] text-muted-foreground">
								Kolejnosc: {activeGroupBy.map((id) => columnLabels[id] ?? id).join(" -> ")}
							</p>
						</div>
					)}
				</div>
			</DropdownPanel>
		</div>
	);
}

/* ============================================================================
   Filter functions
   ============================================================================ */

function ColumnColorInput({
	color,
	label,
	onCommit,
}: {
	color: string;
	label: string;
	onCommit: (color: string) => void;
}) {
	const inputRef = React.useRef<HTMLInputElement>(null);
	const applyColor = useDebouncedCallback(onCommit, 500);
	React.useEffect(() => () => applyColor.cancel(), [applyColor]);
	React.useEffect(() => {
		if (inputRef.current) inputRef.current.value = color;
	}, [color]);
	React.useEffect(() => {
		const input = inputRef.current;
		if (!input) return;
		// Apply immediately on native confirmation; input previews wait for a pause.
		const commit = () => {
			applyColor.cancel();
			onCommit(normalizeColumnColor(input.value));
		};
		input.addEventListener("change", commit);
		return () => input.removeEventListener("change", commit);
	}, [onCommit, applyColor]);
	return (
		<input
			ref={inputRef}
			type="color"
			defaultValue={color}
			onInput={(event) => applyColor(normalizeColumnColor(event.currentTarget.value))}
			aria-label={`Kolor tla kolumny ${label}`}
			className="h-6 w-10 shrink-0 cursor-pointer rounded border border-border bg-transparent p-0.5"
		/>
	);
}

function ColumnColorDropdown<TData extends RowData>({
	columns,
	columnLabels,
	columnColors,
	onColumnColorsChange,
}: {
	columns: Column<TData, unknown>[];
	columnLabels: Record<string, string>;
	columnColors: ColumnColors;
	onColumnColorsChange: (colors: ColumnColors) => void;
}) {
	const [open, setOpen] = React.useState(false);
	const [search, setSearch] = React.useState("");
	const panelId = React.useId();
	const containerRef = useOutsideClick(React.useCallback(() => setOpen(false), []));
	const normalizedSearch = search.trim().toLocaleLowerCase("pl-PL");
	const colorable = columns.filter((column) =>
		`${columnLabels[column.id] ?? column.id} ${column.id}`
			.toLocaleLowerCase("pl-PL")
			.includes(normalizedSearch),
	);
	const coloredCount = Object.keys(columnColors).length;

	function toggle(id: string) {
		const willBeActive = !(id in columnColors);
		tableBreadcrumb("columnColor.toggle", {
			col: id,
			active: willBeActive,
			count: willBeActive ? coloredCount + 1 : coloredCount - 1,
		});
		const next = { ...columnColors };
		if (willBeActive) next[id] = DEFAULT_COLUMN_COLOR;
		else delete next[id];
		onColumnColorsChange(next);
	}

	return (
		<div ref={containerRef} className="relative">
			<ToolbarButton
				icon={<Palette className="size-3" />}
				label={coloredCount > 0 ? `Koloruj (${coloredCount})` : "Koloruj kolumny"}
				onClick={() => setOpen((o) => !o)}
				active={coloredCount > 0}
				hasChevron
				ariaExpanded={open}
				ariaControls={panelId}
			/>
			<DropdownPanel id={panelId} open={open} anchorRef={containerRef} minWidth={220}>
				<div className="p-1.5">
					<div className="mb-1.5 flex items-center justify-between px-1">
						<span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
							Kolorowanie kolumn
						</span>
						{coloredCount > 0 && (
							<button
								type="button"
								onClick={() => {
									tableBreadcrumb("columnColor.clear", { was: coloredCount });
									onColumnColorsChange({});
								}}
								className="text-[10px] text-primary hover:underline"
							>
								Wyczysc
							</button>
						)}
					</div>
					<Input
						type="search"
						value={search}
						onChange={(event) => setSearch(event.target.value)}
						placeholder="Szukaj kolumny..."
						aria-label="Szukaj kolumny do kolorowania"
						className="mb-1.5 h-7 text-xs"
					/>
					<div className="max-h-64 overflow-y-auto">
						{colorable.map((col) => {
							const label = columnLabels[col.id] ?? col.id;
							const color = columnColors[col.id];
							const isActive = color !== undefined;
							return (
								<div
									key={col.id}
									className="flex cursor-pointer select-none items-center gap-2 rounded px-2 py-1.5 text-xs transition-colors hover:bg-muted"
								>
									<label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2">
										<input
											type="checkbox"
											checked={isActive}
											onChange={() => toggle(col.id)}
											className="size-3 cursor-pointer accent-primary"
										/>
										<span
											className={cn(
												"truncate",
												isActive ? "font-medium text-foreground" : "text-muted-foreground",
											)}
										>
											{label}
										</span>
									</label>
									{isActive && (
										<ColumnColorInput
											color={color}
											label={label}
											onCommit={(nextColor) =>
												onColumnColorsChange({
													...columnColors,
													[col.id]: nextColor,
												})
											}
										/>
									)}
								</div>
							);
						})}
						{colorable.length === 0 && (
							<p className="px-2 py-3 text-center text-xs text-muted-foreground">Brak kolumn</p>
						)}
					</div>
				</div>
			</DropdownPanel>
		</div>
	);
}

/**
 * Filtr kolumny przypisywany przez DataTable. Opisany strukturalnie (wiersz
 * z `getValue`), a nie przez `FilterFn<TFeatures, TData>` z v9, bo jedna
 * funkcja obsluguje kolumny wszystkich tabel niezaleznie od typu wiersza.
 */
type DataTableFilterFn = ((
	row: DataTableValueRow,
	columnId: string,
	filterValue: unknown,
) => boolean) & {
	autoRemove?: (value: unknown) => boolean;
};

/** Text filter with "!" support: "!" = not null/empty, otherwise multi-word case-insensitive match */
const textWithBangFilter: DataTableFilterFn = (row, columnId, filterValue) => {
	const filter = String(filterValue ?? "")
		.trim()
		.toLowerCase();
	if (!filter) return true;

	// Special single-char operators: handled server-side, pass through client-side
	if (filter === "!" || filter === "?" || filter === "@") {
		return true;
	}

	const cellValue = String(row.getValue(columnId) ?? "")
		.trim()
		.toLowerCase();

	const words = filter.split(/\s+/).filter(Boolean);
	return words.every((word) => cellValue.includes(word));
};
textWithBangFilter.autoRemove = (val: unknown) => !val;

const numberRangeFilter: DataTableFilterFn = (row, columnId, filterValue) => {
	const value = row.getValue(columnId) as number;
	return matchesNumericFilter(value, filterValue as NumericFilterValue);
};

/* ── Date filter ── */

const DATE_ISO_RE = /^\d{4}-\d{2}-\d{2}$/;
const DATE_DOT_RE = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;
const DATE_OPERATORS = [">=", "<=", ">", "<", "="] as const;

/** Normalize dd.mm.yyyy or yyyy-mm-dd to ISO yyyy-mm-dd for comparison */
function normalizeDateInput(raw: string): string | undefined {
	const trimmed = raw.trim();
	if (DATE_ISO_RE.test(trimmed)) return trimmed;
	const dotMatch = trimmed.match(DATE_DOT_RE);
	if (dotMatch) {
		const [, d, m, y] = dotMatch;
		return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
	}
	return undefined;
}

function parseDateFilter(raw: string): { operator: string; date: string } | undefined {
	const trimmed = raw.trim();
	if (!trimmed) return undefined;

	for (const op of DATE_OPERATORS) {
		if (trimmed.startsWith(op)) {
			const dateStr = normalizeDateInput(trimmed.slice(op.length));
			if (dateStr) return { operator: symbolToOp(op), date: dateStr };
		}
	}

	const dateStr = normalizeDateInput(trimmed);
	if (dateStr) return { operator: "eq", date: dateStr };

	return undefined;
}

const dateRangeFilter: DataTableFilterFn = (row, columnId, filterValue) => {
	const raw = row.getValue(columnId);
	if (raw == null) return false;
	const cellStr = String(raw);
	if (cellStr === "") return false;
	const cellDate = cellStr.slice(0, 10);
	const { date, operator } = filterValue as { date?: string; operator?: string };

	if (!date) return true;

	if (operator === "gt") return cellDate > date;
	if (operator === "gte") return cellDate >= date;
	if (operator === "lt") return cellDate < date;
	if (operator === "lte") return cellDate <= date;
	if (operator === "eq") return cellDate === date;

	return true;
};
dateRangeFilter.autoRemove = (val: unknown) => !val;

/**
 * Array-membership filter used by columns that declare `meta.facetedOptions`.
 * Filter value is `string[]` — row passes when the cell value is included in
 * the selected set. An empty/undefined array means "no filter".
 */
const facetedFilter: DataTableFilterFn = (row, columnId, filterValue) => {
	if (!Array.isArray(filterValue) || filterValue.length === 0) return true;
	const cellValue = row.getValue(columnId);
	if (Array.isArray(cellValue)) {
		return cellValue.some((value) => (filterValue as string[]).includes(String(value)));
	}
	return (filterValue as string[]).includes(String(cellValue ?? ""));
};
facetedFilter.autoRemove = (val: unknown) => !Array.isArray(val) || val.length === 0;

/* ============================================================================
   Column Header Components
   ============================================================================ */

/**
 * Context: passes drag listeners + attributes + handle ref setter from SortableHeaderWrapper
 * down to DataTableColumnHeader so they can be applied ONLY to the GripVertical
 * drag handle — not to the entire header wrapper.
 *
 * Critical: dnd-kit spreads tabIndex=0, role="button", aria-* via `attributes`.
 * If those land on the header wrapper div, Chrome may focus it during React DOM
 * reconciliation (e.g. after data reload), stealing focus from the filter input.
 * Solution: route BOTH listeners AND attributes through context → apply only on grip icon.
 */
const DragHandleContext = React.createContext<{
	dragListeners: ReturnType<typeof useSortable>["listeners"];
	dragAttributes: ReturnType<typeof useSortable>["attributes"];
	setHandleRef: (el: HTMLElement | null) => void;
} | null>(null);

// Outer header-cell wrapper for sortable (non-pinned) columns. Applies the drag
// transform to the FULL header cell — width, filter input, resize handle — so
// the entire header shifts together with its data cells via SortableDataCell.
// Previously useSortable was called on an inner div which caused only the title
// area to move, leaving the outer cell in place.
// Focus-stealing invariant: listeners/attributes are routed through
// DragHandleContext to the grip only — NOT spread onto this wrapper div.
function SortableHeaderWrapper({
	columnId,
	className,
	style,
	children,
}: {
	columnId: string;
	className: string;
	style: React.CSSProperties;
	children: React.ReactNode;
}) {
	const {
		attributes,
		listeners,
		setNodeRef,
		setActivatorNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: columnId });
	const mergedStyle: React.CSSProperties = {
		...style,
		transform: CSS.Translate.toString(transform),
		transition,
		// Dragged header rides the cursor via its own transform with a subtle
		// ghost effect. Siblings shift via SortableContext to open the gap.
		// This matches TanStack's canonical column-dnd pattern — no DragOverlay.
		opacity: isDragging ? 0.85 : style.opacity,
		zIndex: isDragging ? 2 : style.zIndex,
	};
	return (
		<DragHandleContext.Provider
			value={{
				dragListeners: listeners,
				dragAttributes: attributes,
				setHandleRef: setActivatorNodeRef,
			}}
		>
			<div ref={setNodeRef} className={className} style={mergedStyle}>
				{children}
			</div>
		</DragHandleContext.Provider>
	);
}

// Canonical TanStack pattern: each data cell calls useSortable with the SAME id
// as its header, so dnd-kit computes identical transforms for header and body.
// When a column is dragged, every cell in that column receives the drag transform
// and siblings shift horizontally to create a visible gap across the whole table.
// Only used for non-pinned, non-alwaysLast cells — pinned stays sticky.
function SortableDataCell({
	columnId,
	className,
	style,
	children,
}: {
	columnId: string;
	className?: string;
	style?: React.CSSProperties;
	children: React.ReactNode;
}) {
	const { setNodeRef, transform, transition, isDragging } = useSortable({
		id: columnId,
	});
	const mergedStyle: React.CSSProperties = {
		...style,
		transform: CSS.Translate.toString(transform),
		transition,
		// Cell rides the cursor via transform with a subtle ghost effect —
		// matches SortableHeaderWrapper. Siblings shift via SortableContext.
		opacity: isDragging ? 0.85 : style?.opacity,
		zIndex: isDragging ? 2 : style?.zIndex,
	};
	return (
		<div ref={setNodeRef} className={className} style={mergedStyle}>
			{children}
		</div>
	);
}

// Consumer lives inside SortableHeaderWrapper's provider subtree so useContext
// resolves to the real drag props. DataTableColumnHeader itself is the provider's
// parent — reading the context there would always return null.
function DragGrip({ label }: { label: string }) {
	const dragHandle = React.useContext(DragHandleContext);
	return (
		<button
			type="button"
			ref={dragHandle?.setHandleRef as React.RefCallback<HTMLButtonElement>}
			{...dragHandle?.dragAttributes}
			{...dragHandle?.dragListeners}
			aria-label={`Przeciagnij kolumne ${label}`}
			className="flex cursor-grab bg-transparent border-0 p-0 appearance-none"
		>
			<GripVertical className="size-3 shrink-0 text-muted-foreground/50" />
		</button>
	);
}

export interface DataTableColumnHeaderProps<TData extends RowData, TValue> {
	column: Column<TData, TValue>;
	title: string;
	isNumeric?: boolean;
	enableDrag?: boolean;
	enablePinning?: boolean;
	/** Opcjonalny opis kolumny pokazany w tooltipie pod ikona "?" obok tytulu. */
	helpText?: string;
}

export function DataTableColumnHeader<TData extends RowData, TValue>({
	column,
	title,
	isNumeric = false,
	enableDrag = true,
	enablePinning = true,
	helpText,
}: DataTableColumnHeaderProps<TData, TValue>) {
	const canSort = column.getCanSort();
	const isSorted = column.getIsSorted();
	const isPinned = column.getIsPinned();
	const { mobileHeader } = React.useContext(DataTableMobileContext);

	// Telefon, tryb „Tabela”: naglowek jest samym tytulem wewnatrz przycisku menu
	// kolumny (sortowanie, filtr, przypiecie, ukrycie) — bez wlasnych przyciskow.
	if (mobileHeader) {
		return (
			<span
				className={cn(
					"flex min-w-0 items-center gap-0.5 text-[11px] font-semibold uppercase tracking-wide",
					isSorted ? "text-foreground" : "text-muted-foreground",
					isNumeric && "justify-end",
				)}
			>
				<span className="truncate">{title}</span>
				{isSorted === "asc" ? (
					<ArrowUp className="size-3 shrink-0 text-primary" />
				) : isSorted === "desc" ? (
					<ArrowDown className="size-3 shrink-0 text-primary" />
				) : null}
			</span>
		);
	}

	// alwaysLast columns are pinned to the right by orderedColumns; letting users
	// drag them produces a visual snap-back because the order is immediately
	// reasserted. Disable drag for them regardless of enableDrag prop.
	// Pinned columns are sticky-positioned and excluded from the sortable context
	// (sticky + transform conflicts visually) — also not draggable.
	const isAlwaysLast = isAlwaysLastColumn(column);
	const dragEnabled = enableDrag && !isAlwaysLast && !isPinned;

	const handleTogglePin = (e: React.MouseEvent) => {
		e.stopPropagation();
		// v9: logiczne regiony `start`/`end` zamiast `left`/`right` (LTR: start = lewa).
		column.pin(isPinned ? false : "start");
	};

	// NOTE: Filter inputs are NOT rendered here. They live in ColumnFilterInput,
	// rendered by renderHeaderCell OUTSIDE of flexRender. This is critical because
	// flexRender treats inline header lambdas as component types — when columns
	// prop changes, new lambdas = new types = React re-mounts = useState/useRef lost.
	// By rendering filters outside flexRender with a stable key={column.id},
	// they survive column definition changes.

	const titleRow = (
		<div className={cn("group flex h-5 min-w-0 items-center gap-1", isNumeric && "justify-end")}>
			{dragEnabled && <DragGrip label={title} />}

			{enablePinning && (
				<button
					type="button"
					onClick={handleTogglePin}
					className={cn(
						"shrink-0 rounded p-1 transition-colors",
						isPinned
							? "text-primary hover:bg-primary/10"
							: "text-muted-foreground/30 hover:bg-muted hover:text-muted-foreground",
					)}
					title={isPinned ? "Odepnij kolumne" : "Przypnij kolumne"}
				>
					{isPinned ? <PinOff className="size-3" /> : <Pin className="size-3" />}
				</button>
			)}

			{canSort ? (
				<button
					type="button"
					className={cn(
						"flex min-w-0 items-center gap-0.5 text-[11px] font-semibold uppercase tracking-wide hover:text-foreground",
						isSorted ? "text-foreground" : "text-muted-foreground",
					)}
					onClick={() => {
						if (!isSorted) column.toggleSorting(false);
						else if (isSorted === "asc") column.toggleSorting(true);
						else column.clearSorting();
					}}
					title={title}
				>
					<span className="truncate">{title}</span>
					{isSorted === "asc" ? (
						<ArrowUp className="size-3 shrink-0 text-primary" />
					) : isSorted === "desc" ? (
						<ArrowDown className="size-3 shrink-0 text-primary" />
					) : (
						<ArrowUpDown className="size-3 shrink-0 opacity-30" />
					)}
				</button>
			) : (
				<span
					className="truncate text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
					title={title}
				>
					{title}
				</span>
			)}

			{helpText && (
				<Tooltip>
					<TooltipTrigger asChild>
						<button
							type="button"
							className="shrink-0 p-0.5 text-muted-foreground/40 transition-colors hover:text-muted-foreground"
							tabIndex={-1}
						>
							<HelpCircle className="size-3" />
						</button>
					</TooltipTrigger>
					<TooltipContent side="bottom" className="max-w-64 text-left text-[10px] leading-relaxed">
						{helpText}
					</TooltipContent>
				</Tooltip>
			)}
		</div>
	);

	// Sortable wrapping lives in renderHeaderCell (SortableHeaderWrapper) so the
	// transform applies to the full outer header cell (width, filter input, resize
	// handle) — not just the title row. DataTableColumnHeader only renders content.
	return <div className="flex flex-col">{titleRow}</div>;
}

/**
 * Telefon, tryb „Tabela”: caly naglowek kolumny jest przyciskiem menu
 * (sortowanie, filtr, przypiecie, ukrycie) — zamiast drobnych ikon i wiersza
 * filtrow, ktore na 360 px zjadaly polowe ekranu. Tresc naglowka strony jest
 * `inert`, wiec stukniecie zawsze trafia w menu.
 */
function MobileColumnHeaderMenu<TData extends RowData>({
	column,
	label,
	help,
	title,
	isNumeric,
	onFilter,
}: {
	column: Column<TData, unknown>;
	label: string;
	/** Opis kolumny (`helpText` naglowka) — na desktopie tooltip „?”. */
	help?: string;
	title: React.ReactNode;
	isNumeric: boolean;
	onFilter: () => void;
}) {
	const canSort = column.getCanSort();
	const canFilter = column.getCanFilter() || !!column.columnDef.meta?.filterRender;
	const canHide = column.getCanHide();
	const isSorted = column.getIsSorted();
	const isFiltered = column.getIsFiltered();
	const content = (
		<span className={cn("flex min-w-0 flex-1 items-center gap-1", isNumeric && "justify-end")}>
			<span inert className="min-w-0 truncate">
				{title}
			</span>
			{isFiltered && <FilterIcon className="size-3 shrink-0 text-primary" aria-hidden />}
		</span>
	);
	// Kolumna bez sortowania, filtra i ukrywania (np. „Zaznacz wszystkie”) nie ma
	// czego pokazac w menu — jej naglowek zostaje aktywny, z wlasnymi kontrolkami.
	if (!canSort && !canFilter && !canHide) {
		return (
			<div className={cn("flex min-h-9 items-center", isNumeric && "justify-end")}>{title}</div>
		);
	}
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				{/* biome-ignore lint/a11y/useSemanticElements: tresc naglowka strony moze zawierac przyciski — <button> nie moze ich zagniezdzac */}
				<div
					role="button"
					tabIndex={0}
					aria-label={`Kolumna ${label}`}
					data-testid={`mobile-column-menu-${column.id}`}
					className="-mx-2 -my-1.5 flex min-h-9 cursor-pointer items-center px-2 py-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
				>
					{content}
				</div>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="start" className="min-w-52">
				<div className="truncate px-2 py-1.5 text-xs font-semibold text-muted-foreground">
					{label}
				</div>
				{help && (
					<p className="max-w-64 px-2 pb-1.5 text-[11px] leading-snug text-muted-foreground">
						{help}
					</p>
				)}
				{canSort && (
					<>
						<DropdownMenuItem
							className="min-h-10"
							onSelect={() => column.toggleSorting(true, false)}
						>
							<ArrowDown className={cn("size-4", isSorted === "desc" && "text-primary")} />
							Sortuj malejąco
						</DropdownMenuItem>
						<DropdownMenuItem
							className="min-h-10"
							onSelect={() => column.toggleSorting(false, false)}
						>
							<ArrowUp className={cn("size-4", isSorted === "asc" && "text-primary")} />
							Sortuj rosnąco
						</DropdownMenuItem>
						{isSorted && (
							<DropdownMenuItem className="min-h-10" onSelect={() => column.clearSorting()}>
								<ArrowUpDown className="size-4" />
								Wyczyść sortowanie
							</DropdownMenuItem>
						)}
					</>
				)}
				{(canFilter || canHide) && canSort && <DropdownMenuSeparator />}
				{canFilter && (
					<DropdownMenuItem className="min-h-10" onSelect={onFilter}>
						<FilterIcon className={cn("size-4", isFiltered && "text-primary")} />
						{isFiltered ? "Zmień filtr" : "Filtruj"}
					</DropdownMenuItem>
				)}
				{canHide && (
					<DropdownMenuItem className="min-h-10" onSelect={() => column.toggleVisibility(false)}>
						<Columns3 className="size-4" />
						Ukryj kolumnę
					</DropdownMenuItem>
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

function FilterHelpTooltip({
	isNumeric,
	isDate,
	filterMode,
	minChars,
	filterHints,
}: {
	isNumeric: boolean;
	isDate?: boolean;
	filterMode?: "smart" | "progressive";
	minChars?: number;
	filterHints?: string[];
}) {
	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<button
					type="button"
					className="shrink-0 p-0.5 text-muted-foreground/40 hover:text-muted-foreground transition-colors"
					tabIndex={-1}
				>
					<HelpCircle className="size-3" />
				</button>
			</TooltipTrigger>
			<TooltipContent side="bottom" className="max-w-56 text-left">
				{filterHints ? (
					<div className="text-[10px] leading-relaxed space-y-0.5">
						{filterHints.map((hint) => (
							<p key={hint}>{hint}</p>
						))}
					</div>
				) : isDate ? (
					<p className="font-mono text-[10px] leading-relaxed">
						{">15.03.2026  >=2026-03-15"}
						<br />
						{"<01.04.2026  <=2026-04-01"}
						<br />
						{"15.03.2026 — dokladna data"}
					</p>
				) : isNumeric ? (
					<p className="font-mono text-[10px] leading-relaxed">
						{"26,71 — progresywnie (26 → 26,7 → 26,71)"}
						<br />
						{">100  >=100  <50  <=50  =100"}
					</p>
				) : (
					<div className="text-[10px] leading-relaxed space-y-0.5">
						<p>
							<span className="font-mono">!</span> — tylko niepuste
						</p>
						{filterMode === "smart" && <p>Literowki — automatyczne podpowiedzi</p>}
						{filterMode === "progressive" && minChars && <p>Szukanie od {minChars} znakow</p>}
						{!filterMode && <p>Wiele slow — szuka wszystkich</p>}
					</div>
				)}
			</TooltipContent>
		</Tooltip>
	);
}

/**
 * Multi-select popover for columns declaring `meta.facetedOptions`.
 * Renders a trigger button with count badge, a searchable list of options
 * with checkbox indicators, and a "Wyczyść" clear button. Selection changes
 * commit to `onChange` immediately.
 */
interface FacetedFilterPopoverProps {
	title: string;
	options: { label: string; value: string }[];
	selected: string[];
	onChange: (values: string[]) => void;
}

function FacetedFilterPopover({ title, options, selected, onChange }: FacetedFilterPopoverProps) {
	const [open, setOpen] = React.useState(false);
	const [search, setSearch] = React.useState("");

	const filteredOptions = React.useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return options;
		return options.filter((o) => o.label.toLowerCase().includes(q));
	}, [options, search]);

	const selectedSet = React.useMemo(() => new Set(selected), [selected]);

	const toggle = (value: string) => {
		const next = selectedSet.has(value)
			? selected.filter((v) => v !== value)
			: [...selected, value];
		onChange(next);
	};

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<button
					type="button"
					aria-label={`Filtruj ${title}`}
					className={cn(
						"flex h-7 w-full items-center gap-1 rounded-md border border-border bg-card px-2 text-xs",
						"text-foreground hover:bg-accent focus:outline-none focus:ring-1 focus:ring-ring",
					)}
				>
					<FilterIcon className="h-3 w-3 shrink-0 text-muted-foreground" />
					<span className="truncate">{title}</span>
					{selected.length > 0 && (
						<span
							data-testid="faceted-filter-count"
							className="ml-auto inline-flex h-4 min-w-4 items-center justify-center rounded bg-primary px-1 text-[10px] font-medium text-primary-foreground"
						>
							{selected.length}
						</span>
					)}
				</button>
			</PopoverTrigger>
			<PopoverContent
				align="start"
				className="w-60 p-0"
				onPointerDown={(e) => e.stopPropagation()}
				onKeyDown={(e) => e.stopPropagation()}
			>
				<div className="border-b border-border p-2">
					<Input
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder="Szukaj..."
						aria-label={`Szukaj w ${title}`}
						className="h-7 text-xs"
					/>
				</div>
				<div className="max-h-60 overflow-y-auto py-1" role="listbox" aria-multiselectable="true">
					{filteredOptions.length === 0 ? (
						<div className="px-3 py-2 text-xs text-muted-foreground">Brak wynikow</div>
					) : (
						filteredOptions.map((option) => {
							const isSelected = selectedSet.has(option.value);
							return (
								<button
									key={option.value}
									type="button"
									role="option"
									aria-selected={isSelected}
									onClick={() => toggle(option.value)}
									className={cn(
										"flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs",
										"hover:bg-accent focus:bg-accent focus:outline-none",
									)}
								>
									<span
										className={cn(
											"flex h-4 w-4 shrink-0 items-center justify-center rounded border",
											isSelected
												? "border-primary bg-primary text-primary-foreground"
												: "border-border bg-card",
										)}
										aria-hidden="true"
									>
										{isSelected && <Check className="h-3 w-3" />}
									</span>
									<span className="truncate">{option.label}</span>
								</button>
							);
						})
					)}
				</div>
				{selected.length > 0 && (
					<div className="border-t border-border p-1">
						<button
							type="button"
							onClick={() => onChange([])}
							className={cn(
								"flex h-7 w-full items-center justify-center rounded text-xs",
								"text-muted-foreground hover:bg-accent hover:text-foreground",
							)}
						>
							Wyczysc
						</button>
					</div>
				)}
			</PopoverContent>
		</Popover>
	);
}

/**
 * Standalone filter input for column headers. Rendered by renderHeaderCell
 * OUTSIDE of flexRender so it has a stable key={column.id} and never re-mounts
 * when column definitions change (inline header lambdas create new function
 * references → flexRender treats them as new component types → React unmounts).
 *
 * This component owns its local text state and debounces updates to TanStack Table.
 */
function ColumnFilterInput<TData extends RowData>({
	column,
	label,
}: {
	column: Column<TData, unknown>;
	label: string;
}) {
	const custom = column.columnDef.meta?.filterRender;
	const facetedOptions = column.columnDef.meta?.facetedOptions;
	const currentFacetedValue = column.getFilterValue();

	if (custom) {
		return (
			<div
				role="none"
				className="mt-1.5 h-8 border-t border-border pt-1.5"
				onPointerDown={(e) => e.stopPropagation()}
				onKeyDown={(e) => e.stopPropagation()}
			>
				{custom()}
			</div>
		);
	}

	if (facetedOptions) {
		const selected = Array.isArray(currentFacetedValue) ? (currentFacetedValue as string[]) : [];
		const title = typeof column.columnDef.header === "string" ? column.columnDef.header : label;
		return (
			<div
				role="none"
				className="mt-1.5 h-8 border-t border-border pt-1.5"
				onPointerDown={(e) => e.stopPropagation()}
				onKeyDown={(e) => e.stopPropagation()}
			>
				<FacetedFilterPopover
					title={title}
					options={facetedOptions}
					selected={selected}
					onChange={(vals) => column.setFilterValue(vals.length > 0 ? vals : undefined)}
				/>
			</div>
		);
	}

	return <ColumnFilterScalarInput column={column} label={label} />;
}

function ColumnFilterScalarInput<TData extends RowData>({
	column,
	label,
}: {
	column: Column<TData, unknown>;
	label: string;
}) {
	const isNumeric = !!column.columnDef.meta?.isNumeric;
	const isDate = !!column.columnDef.meta?.isDate;
	// Text columns (not numeric, not date, not faceted) default to progressive
	// filterMode with minChars=2 — gives consistent UX across all pages. Opt out
	// by passing meta.filterMode explicitly (e.g. "smart" for typo-tolerant match).
	const isText = !column.columnDef.meta?.isNumeric && !column.columnDef.meta?.isDate;
	const filterMode = column.columnDef.meta?.filterMode ?? (isText ? "progressive" : undefined);
	const minChars =
		column.columnDef.meta?.minChars ?? (filterMode === "progressive" ? 2 : undefined);
	const filterHints = column.columnDef.meta?.filterHints;
	const currentFilterValue = column.getFilterValue();

	// Numeric/date filter: local state (display string like ">=100" or ">15.03.2026")
	const [filterInput, setFilterInput] = React.useState<string>(() => {
		if (isNumeric && currentFilterValue) {
			const { min, operator } = currentFilterValue as { min?: number; operator?: string };
			if (min !== undefined) return `${opToSymbol(operator ?? "eq")}${min}`;
		}
		if (isDate && currentFilterValue) {
			const { date, operator } = currentFilterValue as { date?: string; operator?: string };
			if (date) return `${opToSymbol(operator ?? "eq", "")}${date}`;
		}
		return "";
	});

	// Text filter: local state + debounced table update
	const [textInput, setTextInput] = React.useState<string>(() =>
		!isNumeric && !isDate ? ((currentFilterValue as string) ?? "") : "",
	);
	const textDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
	const numericDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
	const pendingWriteRef = React.useRef<(() => void) | null>(null);
	const pendingWrites = React.useContext(PendingFilterWritesContext);
	const [isFocused, setIsFocused] = React.useState(false);

	React.useEffect(() => {
		if (!pendingWrites) return;
		const cancelTimers = () => {
			if (textDebounceRef.current) clearTimeout(textDebounceRef.current);
			if (numericDebounceRef.current) clearTimeout(numericDebounceRef.current);
		};
		const write: PendingFilterWrite = {
			commit: () => {
				cancelTimers();
				const pending = pendingWriteRef.current;
				pendingWriteRef.current = null;
				pending?.();
			},
			discard: () => {
				cancelTimers();
				pendingWriteRef.current = null;
			},
		};
		pendingWrites.add(write);
		return () => {
			pendingWrites.delete(write);
		};
	}, [pendingWrites]);

	// Sync external → local: when the column filter value changes from outside
	// (e.g. URL hydration, controlled prop update, or toolbar reset) reflect it
	// in the input UI — but only when the user isn't actively editing it, so we
	// don't fight their typing.
	React.useEffect(() => {
		if (isFocused) return;
		if (currentFilterValue === undefined) {
			setFilterInput((cur) => (cur === "" ? cur : ""));
			setTextInput((cur) => (cur === "" ? cur : ""));
			return;
		}
		if (!isNumeric && !isDate) {
			const next = typeof currentFilterValue === "string" ? currentFilterValue : "";
			setTextInput((cur) => (cur === next ? cur : next));
		} else if (isNumeric) {
			const { min, operator } = currentFilterValue as { min?: number; operator?: string };
			const next = min !== undefined ? `${opToSymbol(operator ?? "eq")}${min}` : "";
			setFilterInput((cur) => (cur === next ? cur : next));
		} else if (isDate) {
			const { date, operator } = currentFilterValue as { date?: string; operator?: string };
			const next = date ? `${opToSymbol(operator ?? "eq", "")}${date}` : "";
			setFilterInput((cur) => (cur === next ? cur : next));
		}
	}, [currentFilterValue, isFocused, isNumeric, isDate]);

	React.useEffect(() => {
		return () => {
			if (textDebounceRef.current) clearTimeout(textDebounceRef.current);
			if (numericDebounceRef.current) clearTimeout(numericDebounceRef.current);
		};
	}, []);

	const handleTextFilter = (value: string) => {
		setTextInput(value);
		if (textDebounceRef.current) clearTimeout(textDebounceRef.current);
		const write = () => {
			pendingWriteRef.current = null;
			// Fall back to the derived default (progressive → 2) so text columns
			// without explicit meta.minChars still gate properly.
			const effectiveMinChars = column.columnDef.meta?.minChars ?? minChars ?? 0;
			const trimmed = value.trim();
			if (
				trimmed.length > 0 &&
				trimmed.length < effectiveMinChars &&
				!["!", "?", "@"].includes(trimmed)
			) {
				return;
			}
			column.setFilterValue(value || undefined);
		};
		pendingWriteRef.current = write;
		textDebounceRef.current = setTimeout(write, 300);
	};

	const handleNumericFilter = (value: string) => {
		setFilterInput(value);
		if (numericDebounceRef.current) clearTimeout(numericDebounceRef.current);
		const write = () => {
			pendingWriteRef.current = null;
			column.setFilterValue(parseNumericFilter(value) ?? undefined);
		};
		pendingWriteRef.current = write;
		numericDebounceRef.current = setTimeout(write, 200);
	};

	const handleDateFilter = (value: string) => {
		setFilterInput(value);
		column.setFilterValue(parseDateFilter(value) ?? undefined);
	};

	const handleChange = isDate
		? handleDateFilter
		: isNumeric
			? handleNumericFilter
			: handleTextFilter;
	const inputValue = isNumeric || isDate ? filterInput : textInput;

	return (
		<div
			role="none"
			className="mt-1.5 h-8 border-t border-border pt-1.5"
			onPointerDown={(e) => e.stopPropagation()}
			onKeyDown={(e) => e.stopPropagation()}
		>
			<div className="flex items-center gap-0.5">
				<Input
					className="h-7 w-full min-w-[80px] max-w-full border-border bg-card px-2 text-xs text-foreground placeholder:text-muted-foreground focus:border-ring [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
					value={inputValue}
					onChange={(e) => handleChange(e.target.value)}
					onFocus={() => setIsFocused(true)}
					onBlur={() => setIsFocused(false)}
					placeholder=""
					aria-label={`Filtruj ${label}`}
				/>
				<FilterHelpTooltip
					isNumeric={isNumeric}
					isDate={isDate}
					filterMode={filterMode}
					minChars={minChars}
					filterHints={filterHints}
				/>
			</div>
		</div>
	);
}

/* ============================================================================
   localStorage Helpers
   ============================================================================ */

function storageKey(tableId: string, key: string) {
	return `farmaster_dt_${tableId}_${key}`;
}

function loadFromStorage<T>(tableId: string | undefined, key: string, fallback: T): T {
	if (!tableId || typeof window === "undefined") return fallback;
	try {
		const raw = localStorage.getItem(storageKey(tableId, key));
		return raw ? (JSON.parse(raw) as T) : fallback;
	} catch {
		return fallback;
	}
}

function saveToStorage<T>(tableId: string | undefined, key: string, value: T): void {
	if (!tableId || typeof window === "undefined") return;
	try {
		localStorage.setItem(storageKey(tableId, key), JSON.stringify(value));
	} catch {
		return;
	}
}

/**
 * Schematy zapisane przed migracja na TanStack Table v9 maja pinning
 * `{ left, right }`; po zastosowaniu takiego schematu v9 rzucalby TypeError
 * w `getHeaderGroups()`. Normalizujemy przy odczycie, a nastepny zapis
 * utrwala juz `{ start, end }`.
 */
function normalizeSchemes(value: unknown): TableScheme[] {
	if (!Array.isArray(value)) return [];
	return value
		.filter(
			(scheme): scheme is TableScheme =>
				!!scheme && typeof scheme === "object" && !Array.isArray(scheme),
		)
		.map((scheme) => ({
			...scheme,
			columnPinning: normalizeColumnPinning(scheme.columnPinning, { start: [], end: [] }),
		}));
}

/* ============================================================================
   DataTable Props
   ============================================================================ */

export interface DataTableProps<TData extends RowData> {
	columns: ColumnDef<TData, unknown>[];
	data: TData[];
	tableId?: string;
	/**
	 * Tryb startowy na telefonie, zanim uzytkownik wybierze wlasny. Domyslnie
	 * „Lista”; „table” tylko gdy glownym zadaniem jest porownywanie wielu kolumn.
	 */
	mobileDefaultMode?: TableMobileMode;
	/** Etykieta przycisku `onRowCtrlClick` / `onRowSelect` w szczegolach wiersza na telefonie. */
	mobileOpenLabel?: string;
	enableColumnPinning?: boolean;
	defaultPinnedColumns?: string[];
	showSummaryRow?: boolean;
	summaryData?: Record<string, React.ReactNode>;
	summableColumns?: string[];
	summaryFormatter?: (columnId: string, value: number) => React.ReactNode;
	deriveSummary?: (args: {
		sums: Record<string, number>;
		rowCount: number;
		/** Oryginalne dane przefiltrowanych wierszy — dla sum warunkowych
		 *  (np. suma kolumny A tylko tam, gdzie kolumna B nie jest null). */
		rows: TData[];
	}) => Record<string, React.ReactNode>;
	summaryLabel?: string;
	enableColumnOrdering?: boolean;
	className?: string;
	filterResetKey?: string | number;
	onClearFiltersReady?: (clearFn: () => void) => void;
	onSetColumnFilterReady?: (setFn: (columnId: string, value: string) => void) => void;
	onSelectedRowChange?: (hasSelected: boolean) => void;
	/** Fires with the selected row's original data (or null when deselected). */
	onRowSelect?: (row: TData | null) => void;
	onFilteredRowCountChange?: (count: number) => void;
	onRowCtrlClick?: (rowId: string) => void;
	/**
	 * Klik w wiersz (desktop i telefon) otwiera widok strony zamiast przelaczac
	 * zaznaczenie i zamiast szczegolow wiersza na telefonie. Klikniecia w
	 * kontrolki komorki (link, przycisk, pole) zostaja im.
	 */
	onRowClick?: (rowId: string) => void;
	/** Najechanie na wiersz — np. prefetch tego, co otworzy `onRowClick`. */
	onRowHover?: (rowId: string) => void;
	onScroll?: (scrollTop: number) => void;
	/**
	 * Override TanStack Table's default row id (array index). Pass a function
	 * that returns a stable key from the row data so callbacks like
	 * `onRowCtrlClick` receive a meaningful identifier even in server-paginated
	 * mode where index ≠ entity id.
	 */
	getRowId?: (row: TData, index: number, parent?: Row<TData>) => string;
	/** Server-side filtering: called (debounced) when column filters change */
	onColumnFiltersChange?: (filters: ColumnFiltersState) => void;
	/**
	 * Controlled column filters. When provided together with `onColumnFiltersChange`
	 * the table operates in controlled mode (`state.columnFilters = prop`). When omitted,
	 * the table manages its own internal state and only emits changes via the callback.
	 */
	columnFilters?: ColumnFiltersState;
	/**
	 * When true, disables local filtering (TanStack `manualFiltering`) — the parent
	 * is expected to re-fetch filtered data via `onColumnFiltersChange`. Also flips
	 * the visible-row counter to use `data.length` directly instead of filtered rows.
	 */
	serverFiltering?: boolean;
	/** Server-side sorting: called when sorting state changes */
	onSortingChange?: (sorting: SortingState) => void;
	/**
	 * Controlled sorting. When provided together with `onSortingChange` the table
	 * operates in controlled mode for sorting. When omitted, internal state is used.
	 */
	sorting?: SortingState;
	/**
	 * When true, the caller sorts the data (server-side). When false (default when
	 * sort is controlled but backend has NOT sorted the full set), TanStack sorts
	 * client-side on the visible row set. Default: false.
	 */
	manualSorting?: boolean;
	/** Server-side infinite scroll: called when user scrolls near the bottom */
	onFetchMore?: () => void;
	/** Whether there are more pages to load (controls spinner at the bottom) */
	hasNextPage?: boolean;
	/** Whether a next page is currently being fetched */
	isFetchingNextPage?: boolean;
	/** Whether the whole table is waiting for refreshed data */
	isLoading?: boolean;
	/** Label displayed in the full-table loading overlay */
	loadingLabel?: string;
	onExportError?: (error: Error) => void;
	exportFileName?: string;
	/** Fetch ALL rows from backend for export (ignoring client-side pagination) */
	/** Zrodlo wierszy do eksportu, gdy tabela pokazuje tylko doladowane strony.
	 *  Wariant `{ rows, totalCount }` jest lepszy: `totalCount` z TEJ SAMEJ odpowiedzi
	 *  opisuje ten sam stan bazy co wiersze, wiec straznik kompletnosci porownuje
	 *  liczby bez okna na odswiezenie MV miedzy zadaniami. */
	fetchAllForExport?: () => Promise<
		| Record<string, unknown>[]
		| {
				rows: Record<string, unknown>[];
				totalCount?: number;
				summary?: Record<string, number | null>;
		  }
	>;
	/** Dodatkowe, jawnie podpisane wiersze (np. podsumowania raportu) dopisywane
	 * po przefiltrowanych i posortowanych danych przez ten sam eksporter. */
	exportAdditionalRows?: Record<string, unknown>[];
	/** Exposes the existing Excel export action to a parent-owned trigger. */
	onExportReady?: (exportFn: () => Promise<void>) => void;
	/** Called when export starts/finishes (for disabling buttons, showing spinners) */
	onExportingChange?: (isExporting: boolean) => void;
	/**
	 * Wlasne akcje strony w dolnym pasku, zaraz za eksportem. Pasek jest juz
	 * miejscem, gdzie user szuka operacji na calej tabeli, wiec przycisk
	 * dotyczacy calego zbioru nie ma po co siedziec w gornym pasku filtrow.
	 */
	toolbarActions?: React.ReactNode;
	/** Show the Excel export button in the toolbar */
	enableExport?: boolean;
	virtualizationThreshold?: number;
	rowHeight?: number;
	// Toolbar
	/** Show the feature toolbar above the table header */
	enableToolbar?: boolean;
	/** Show the column visibility toggle button */
	enableColumnVisibility?: boolean;
	/** Show the scheme manager (save/load named column layouts) */
	enableSchemes?: boolean;
	/** Show the grouping control (pre-aggregate rows by text columns) */
	enableGrouping?: boolean;
	/** Controlled group-by state from parent (for server-side grouping) */
	externalGroupBy?: string[];
	/** Callback when group-by changes in controlled mode */
	onGroupByChange?: (groupBy: string[]) => void;
	/** When true, data is already grouped by server — skip client-side processedData */
	serverGrouped?: boolean;
	/**
	 * Map group-key column → label column(s) to carry over from first row.
	 * Single column: `{ issuerNip: "issuerName" }` (KSeF pattern).
	 * Multiple columns: `{ bloz07: ["nazwa", "rodzaj", "idfirm", "apteka"] }`
	 * (DaneTab pattern — gdy wszystkie te kolumny sa stale wewnatrz grupy).
	 */
	groupLabelColumns?: Record<string, string | string[]>;
	/** Show aggregation option checkboxes */
	showAggregationOptions?: boolean;
	/**
	 * Human-readable labels for column IDs used in toolbar dropdowns.
	 * Falls back to column ID if not provided.
	 */
	columnLabels?: Record<string, string>;
	enableColumnColoring?: boolean;
	/** Quick-view badges for summary columns — shown pinned right in the summary row when those columns are scrolled out of view */
	summaryQuickView?: { columnId?: string; label: string; value?: React.ReactNode }[];
	/** Column ID that triggers hiding the quick-view overlay. Defaults to first summaryQuickView columnId. */
	summaryQuickViewHideAt?: string;
	/** Total records on server (for server-side pagination — shows X / Y wierszy) */
	totalCount?: number;
	/** Default column visibility when no saved config exists in localStorage */
	defaultColumnVisibility?: VisibilityState;
	/**
	 * Controlled columnVisibility. When provided together with onColumnVisibilityChange,
	 * the table delegates state ownership to the parent — useful for programmatic
	 * toggles (e.g. auto-show columns when a filter activates) where the internal
	 * lazy-init from localStorage would otherwise require a remount.
	 */
	columnVisibility?: VisibilityState;
	onColumnVisibilityChange?: (next: VisibilityState) => void;
	/** Column IDs to always include in Excel export even when hidden in table */
	alwaysExportColumns?: string[];
	/** Server-side export endpoint (e.g. "/ksef/invoices/export"). When set, export POSTs to backend instead of client-side ExcelJS. */
	exportEndpoint?: string;
	/** Server-side filters to include in the export POST body */
	exportFilters?: Record<string, unknown>;
	/**
	 * Optional per-row class augmentation. Receives the original row data and
	 * returns a className string (or undefined) merged into the row container.
	 * Use for subtle row-level state hints (e.g. excluded/anomaly rows). Per
	 * `feedback_ui_subtlety` keep opacity ≤ 30% and prefer design tokens
	 * (e.g. `bg-warning/5`) over hardcoded colors.
	 */
	rowClassName?: (row: TData) => string | undefined;
}

/* ============================================================================
   Virtual Row
   ============================================================================ */

interface VirtualRowProps<TData extends RowData> {
	row: Row<TData>;
	index: number;
	start: number;
	rowHeight: number;
	orderedColumns: Column<TData, unknown>[];
	/** Reference that changes when column definitions change (e.g. brutto/netto toggle) */
	columnDefs: ColumnDef<TData, unknown>[];
	/** Stringified columnSizing state — forces re-render when column widths change */
	columnSizingKey: string;
	/** Sortable column IDs (excluding pinned + alwaysLast) — wraps cells in SortableContext */
	sortableColumnIds: string[];
	isColumnPinned: (id: string) => boolean;
	isLastPinnedColumn: (id: string) => boolean;
	getLeftOffset: (id: string) => number;
	getPinnedZIndex: (id: string, base: number) => number | undefined;
	pinnedSeparatorClass: string;
	lastPinnedShadowClass: string;
	pinnedColumnIds: string[];
	selectedRowId: string | null;
	onRowClick: (rowId: string, event?: React.MouseEvent<HTMLElement>) => void;
	onRowCtrlClick?: (rowId: string) => void;
	onRowHover?: (rowId: string) => void;
	getCellColorBg: (columnId: string, value: unknown) => React.CSSProperties | undefined;
	rowClassName?: (row: TData) => string | undefined;
}

/**
 * Klik w kontrolke komorki (link, przycisk, pole) albo z portalu (opcje Select,
 * menu w komorce bubbluja przez drzewo Reacta do wiersza, choc w DOM sa poza
 * nim) — nalezy do kontrolki, nie do wiersza.
 */
function isCellControlClick(event?: React.MouseEvent<HTMLElement>): boolean {
	const target = event?.target as HTMLElement | null;
	if (event && target && !event.currentTarget.contains(target)) return true;
	return Boolean(
		target?.closest(
			"button, a, input, select, textarea, label, [role='button'], [role='checkbox'], [role='combobox']",
		),
	);
}

function VirtualRowInner<TData extends RowData>({
	row,
	index,
	start,
	rowHeight,
	orderedColumns,
	columnDefs: _columnDefs,
	columnSizingKey: _columnSizingKey,
	sortableColumnIds,
	isColumnPinned,
	isLastPinnedColumn,
	getLeftOffset,
	getPinnedZIndex,
	pinnedSeparatorClass,
	lastPinnedShadowClass,
	selectedRowId,
	onRowClick,
	onRowCtrlClick,
	onRowHover,
	getCellColorBg,
	rowClassName,
}: VirtualRowProps<TData>) {
	const isEven = index % 2 === 0;
	const isSelected = row.id === selectedRowId;
	const extraRowClass = rowClassName?.(row.original);
	const tinted = !isSelected && !!extraRowClass;

	// biome-ignore lint/correctness/useExhaustiveDependencies: _columnDefs forces recalc when column definitions change (e.g. brutto/netto toggle)
	const cellMap = React.useMemo(() => {
		const m = new Map<string, ReturnType<typeof row.getVisibleCells>[number]>();
		for (const c of row.getVisibleCells()) m.set(c.column.id, c);
		return m;
	}, [row, _columnDefs]);

	return (
		// biome-ignore lint/a11y/useSemanticElements: virtual table row — <tr> incompatible with position:absolute virtualization
		// biome-ignore lint/a11y/useKeyWithClickEvents: row selection is mouse-only by design — keyboard nav removed to prevent focus ring noise
		// biome-ignore lint/a11y/useFocusableInteractive: intentionally non-focusable — prevents green focus rings on every row
		<div
			role="row"
			onClick={(e) => {
				if (e.ctrlKey || e.metaKey) {
					e.preventDefault();
					if (onRowCtrlClick) {
						onRowCtrlClick(row.id);
					} else {
						const btn = e.currentTarget.querySelector<HTMLButtonElement>("button");
						btn?.click();
					}
				} else {
					onRowClick(row.id, e);
				}
			}}
			onMouseEnter={onRowHover ? () => onRowHover(row.id) : undefined}
			className={cn(
				"group transition-colors hover:bg-muted/70 cursor-pointer",
				isSelected && "bg-summary-row",
				extraRowClass,
			)}
			style={{
				position: "absolute",
				top: 0,
				left: 0,
				right: 0,
				height: rowHeight,
				transform: `translateY(${start}px)`,
				display: "flex",
				alignItems: "stretch",
				borderBottom: "1px solid var(--color-border)",
				// Tint z `rowClassName` to klasa Tailwinda, a inline style bije KAZDA
				// klase — bez tego wylaczenia zebra zawsze wygrywala i podswietlenie
				// wiersza bylo niewidoczne (dotyczylo tez Wynikow TOP i Wyprzedazy).
				// Tint powinien byc nieprzezroczysty: przypiete kolumny go dziedzicza.
				background: tinted
					? undefined
					: isSelected
						? "var(--color-summary-row)"
						: isEven
							? "var(--color-card)"
							: "var(--color-secondary)",
			}}
		>
			<SortableContext items={sortableColumnIds} strategy={horizontalListSortingStrategy}>
				{orderedColumns.map((col) => {
					const cell = cellMap.get(col.id);
					if (!cell) return null;

					const pinned = isColumnPinned(cell.column.id);
					const lastPinned = isLastPinnedColumn(cell.column.id);
					const left = getLeftOffset(cell.column.id);
					const zIndex = getPinnedZIndex(cell.column.id, 20);
					// Przypieta kolumna musi byc nieprzezroczysta (przykrywa przewijane
					// komorki), wiec przy tincie dostaje go klasa zamiast inline bg.
					const bgStyle =
						pinned && !tinted
							? {
									background: isSelected
										? "var(--color-summary-row)"
										: isEven
											? "var(--color-card)"
											: "var(--color-secondary)",
								}
							: {};
					const isNumeric = cell.column.columnDef.meta?.isNumeric;
					const cellClassName = cn(
						"flex shrink-0 items-center px-1.5 py-1 sm:px-2 sm:py-1.5 text-[11px] sm:text-sm text-foreground overflow-hidden",
						isNumeric && "justify-end",
						!pinned && "border-r border-border last:border-r-0",
						!pinned && !isSelected && cell.column.columnDef.meta?.cellClassName,
						pinned && "sticky",
						pinned && tinted && extraRowClass,
						pinned && !lastPinned && pinnedSeparatorClass,
						pinned && lastPinned && lastPinnedShadowClass,
						pinned && "group-hover:!bg-muted",
					);
					const cellStyle: React.CSSProperties = {
						width: col.getSize(),
						left: pinned ? left : undefined,
						zIndex,
						...bgStyle,
						...(!isSelected ? (getCellColorBg(cell.column.id, cell.getValue()) ?? {}) : {}),
						flexShrink: 0,
						overflow: "hidden",
						...(cell.column.columnDef.meta?.fill && !pinned ? { flexGrow: 1 } : {}),
					};
					const content = (
						<span className="truncate">
							{flexRender(cell.column.columnDef.cell, cell.getContext())}
						</span>
					);

					if (pinned) {
						return (
							<div key={cell.id} className={cellClassName} style={cellStyle}>
								{content}
							</div>
						);
					}
					return (
						<SortableDataCell
							key={cell.id}
							columnId={cell.column.id}
							className={cellClassName}
							style={cellStyle}
						>
							{content}
						</SortableDataCell>
					);
				})}
			</SortableContext>
		</div>
	);
}

const VirtualRow = React.memo(
	VirtualRowInner,
	(prev, next) =>
		prev.row === next.row &&
		prev.start === next.start &&
		prev.index === next.index &&
		prev.pinnedColumnIds === next.pinnedColumnIds &&
		prev.orderedColumns === next.orderedColumns &&
		prev.sortableColumnIds === next.sortableColumnIds &&
		prev.columnDefs === next.columnDefs &&
		prev.columnSizingKey === next.columnSizingKey &&
		prev.selectedRowId === next.selectedRowId &&
		prev.getCellColorBg === next.getCellColorBg &&
		prev.rowClassName === next.rowClassName &&
		prev.onRowClick === next.onRowClick &&
		prev.onRowHover === next.onRowHover,
) as typeof VirtualRowInner;

/* ============================================================================
   DataTable — Main Component
   ============================================================================ */

export function DataTable<TData extends RowData>({
	columns,
	data,
	tableId,
	mobileDefaultMode,
	mobileOpenLabel = "Otwórz szczegóły",
	enableColumnPinning = true,
	defaultPinnedColumns = [],
	showSummaryRow = false,
	summaryData,
	summableColumns = [],
	summaryFormatter,
	deriveSummary,
	summaryLabel = "SUMA",
	enableColumnOrdering = true,
	className,
	filterResetKey,
	onClearFiltersReady,
	onSetColumnFilterReady,
	onSelectedRowChange,
	onRowSelect,
	onFilteredRowCountChange,
	onRowCtrlClick,
	onRowClick,
	onRowHover,
	onScroll,
	getRowId,
	onColumnFiltersChange,
	columnFilters: columnFiltersProp,
	serverFiltering = false,
	onSortingChange: onSortingChangeProp,
	sorting: sortingProp,
	manualSorting: manualSortingProp,
	onFetchMore,
	hasNextPage,
	isFetchingNextPage,
	isLoading = false,
	loadingLabel = "Pobieranie danych...",
	onExportError,
	exportFileName,
	fetchAllForExport,
	exportAdditionalRows,
	onExportReady,
	onExportingChange,
	enableExport = false,
	virtualizationThreshold = 100,
	rowHeight = 36,
	enableToolbar = false,
	toolbarActions,
	enableColumnVisibility = false,
	enableSchemes = false,
	enableGrouping = false,
	externalGroupBy,
	onGroupByChange: onGroupByChangeProp,
	serverGrouped = false,
	groupLabelColumns = {},
	showAggregationOptions = false,
	columnLabels = {},
	enableColumnColoring = false,
	summaryQuickView,
	summaryQuickViewHideAt,
	totalCount,
	defaultColumnVisibility,
	columnVisibility: columnVisibilityProp,
	onColumnVisibilityChange,
	alwaysExportColumns,
	exportEndpoint,
	exportFilters,
	rowClassName,
}: DataTableProps<TData>) {
	// biome-ignore lint/correctness/useExhaustiveDependencies: columns.map intentionally omitted to preserve initial column order on first render only
	const defaultColumnOrder = React.useMemo(
		() =>
			columns.map(
				(col) => (col as { accessorKey?: string }).accessorKey ?? (col as { id?: string }).id ?? "",
			),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	// Telefon (<md): tryb „Lista” (domyslny) albo „Tabela”, zapamietany per tabela.
	// Desktop nie czyta tego stanu — renderuje sie dokladnie jak wczesniej.
	const isBelowMd = useIsBelowMd();
	const modeStorageId = React.useMemo(
		() => tableId ?? fallbackTableId("dt", defaultColumnOrder),
		[tableId, defaultColumnOrder],
	);
	const [mobileMode, setMobileMode] = useTableMode(modeStorageId, mobileDefaultMode);
	const isMobileList = isBelowMd && mobileMode === "list";
	const isMobileTable = isBelowMd && mobileMode === "table";
	const [mobileDetailsRowId, setMobileDetailsRowId] = React.useState<string | null>(null);
	const [mobileSortOpen, setMobileSortOpen] = React.useState(false);
	const [mobileFilterColumnId, setMobileFilterColumnId] = React.useState<string | null>(null);
	const pendingFilterWrites = React.useMemo(() => new Set<PendingFilterWrite>(), []);
	const [mobileMeasureId, setMobileMeasureId] = React.useState<string | null>(() =>
		loadFromStorage<string | null>(tableId, "mobileMeasure", null),
	);

	// Core table state. Sorting and columnFilters can be controlled by the parent
	// (pass value + onChange). When omitted, internal state is used as before.
	if (process.env.NODE_ENV !== "production") {
		if ((columnFiltersProp !== undefined) !== (onColumnFiltersChange !== undefined)) {
			console.warn(
				"[DataTable] columnFilters and onColumnFiltersChange must be both provided (controlled) or both omitted (uncontrolled). " +
					"Mismatched pair silently falls back to uncontrolled mode.",
			);
		}
		if ((sortingProp !== undefined) !== (onSortingChangeProp !== undefined)) {
			console.warn(
				"[DataTable] sorting and onSortingChange must be both provided (controlled) or both omitted (uncontrolled). " +
					"Mismatched pair silently falls back to uncontrolled mode.",
			);
		}
		if ((columnVisibilityProp !== undefined) !== (onColumnVisibilityChange !== undefined)) {
			console.warn(
				"[DataTable] columnVisibility and onColumnVisibilityChange must be both provided (controlled) or both omitted (uncontrolled). " +
					"Mismatched pair silently falls back to uncontrolled mode.",
			);
		}
	}
	const isSortingControlled = sortingProp !== undefined && !!onSortingChangeProp;
	const isFiltersControlled = columnFiltersProp !== undefined && !!onColumnFiltersChange;

	const [internalSorting, setInternalSorting] = React.useState<SortingState>([]);
	const sorting = isSortingControlled ? (sortingProp as SortingState) : internalSorting;
	const sortingRef = React.useRef(sorting);
	sortingRef.current = sorting;

	const [internalColumnFilters, setInternalColumnFilters] = React.useState<ColumnFiltersState>([]);
	const columnFilters = isFiltersControlled
		? (columnFiltersProp as ColumnFiltersState)
		: internalColumnFilters;

	const handleSortingChange = React.useCallback<
		(updater: SortingState | ((old: SortingState) => SortingState)) => void
	>(
		(updater) => {
			const next =
				typeof updater === "function"
					? (updater as (old: SortingState) => SortingState)(sortingRef.current)
					: updater;
			if (!isSortingControlled) setInternalSorting(next);
			onSortingChangeProp?.(next);
		},
		[isSortingControlled, onSortingChangeProp],
	);

	const columnFiltersRef = React.useRef(columnFilters);
	columnFiltersRef.current = columnFilters;
	const handleColumnFiltersChange = React.useCallback<
		(updater: ColumnFiltersState | ((old: ColumnFiltersState) => ColumnFiltersState)) => void
	>(
		(updater) => {
			const next =
				typeof updater === "function"
					? (updater as (old: ColumnFiltersState) => ColumnFiltersState)(columnFiltersRef.current)
					: updater;
			if (!isFiltersControlled) setInternalColumnFilters(next);
			onColumnFiltersChange?.(next);
		},
		[isFiltersControlled, onColumnFiltersChange],
	);

	// Local helpers that mutate filters via the unified handler so controlled
	// callers also receive the change.
	const setColumnFilters = handleColumnFiltersChange;
	const [columnOrder, setColumnOrder] = React.useState<ColumnOrderState>(() => {
		const saved = loadFromStorage<ColumnOrderState | null>(tableId, "columnOrder", null);
		if (!saved) return defaultColumnOrder;
		// Reconcile: insert any new columns at their default position
		const missing = defaultColumnOrder.filter((id) => !saved.includes(id));
		if (missing.length === 0) return saved;
		const reconciled = [...saved];
		for (const id of missing) {
			const defaultIdx = defaultColumnOrder.indexOf(id);
			// Find the best insertion point: after the preceding default column that exists in saved
			let insertAt = reconciled.length;
			for (let i = defaultIdx - 1; i >= 0; i--) {
				const prevIdx = reconciled.indexOf(defaultColumnOrder[i]);
				if (prevIdx !== -1) {
					insertAt = prevIdx + 1;
					break;
				}
			}
			reconciled.splice(insertAt, 0, id);
		}
		return reconciled;
	});
	const [columnPinning, setColumnPinning] = React.useState<ColumnPinningState>(() =>
		// Zapis sprzed v9 ma `{ left, right }` — patrz normalizeColumnPinning.
		normalizeColumnPinning(loadFromStorage<unknown>(tableId, "columnPinning", null), {
			start: defaultPinnedColumns,
			end: [],
		}),
	);
	const [selectedRowId, setSelectedRowId] = React.useState<string | null>(null);
	const [showScrollTop, setShowScrollTop] = React.useState(false);
	const [canScrollRight, setCanScrollRight] = React.useState(false);
	const toolbarScrollRef = React.useRef<HTMLDivElement>(null);

	const updateToolbarScrollIndicator = React.useCallback(() => {
		const el = toolbarScrollRef.current;
		if (!el) return;
		setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
	}, []);

	React.useEffect(() => {
		const el = toolbarScrollRef.current;
		if (!el) return;
		updateToolbarScrollIndicator();
		el.addEventListener("scroll", updateToolbarScrollIndicator);
		const ro = new ResizeObserver(updateToolbarScrollIndicator);
		ro.observe(el);
		return () => {
			el.removeEventListener("scroll", updateToolbarScrollIndicator);
			ro.disconnect();
		};
	}, [updateToolbarScrollIndicator]);

	// Toolbar state
	const isColumnVisibilityControlled =
		columnVisibilityProp !== undefined && !!onColumnVisibilityChange;
	const [internalColumnVisibility, setInternalColumnVisibility] = React.useState<VisibilityState>(
		() => {
			const saved = loadFromStorage<VisibilityState | null>(tableId, "columnVisibility", null);
			if (saved) return saved;
			return defaultColumnVisibility ?? {};
		},
	);
	const columnVisibility = isColumnVisibilityControlled
		? (columnVisibilityProp as VisibilityState)
		: internalColumnVisibility;
	const setColumnVisibility = React.useCallback<
		(updater: VisibilityState | ((old: VisibilityState) => VisibilityState)) => void
	>(
		(updater) => {
			if (isColumnVisibilityControlled) {
				const next =
					typeof updater === "function"
						? (updater as (old: VisibilityState) => VisibilityState)(
								columnVisibilityProp as VisibilityState,
							)
						: updater;
				onColumnVisibilityChange?.(next);
				return;
			}
			setInternalColumnVisibility(updater);
		},
		[isColumnVisibilityControlled, columnVisibilityProp, onColumnVisibilityChange],
	);
	const [columnSizing, setColumnSizing] = React.useState<ColumnSizingState>(() =>
		loadFromStorage(tableId, "columnSizing", {}),
	);
	const [aggregationConfig, setAggregationConfig] = React.useState<AggregationConfig>(() => ({
		...DEFAULT_AGG_CONFIG,
		...loadFromStorage<Partial<AggregationConfig>>(tableId, "aggregationConfig", {}),
	}));
	const [internalGroupBy, setInternalGroupBy] = React.useState<string[]>(() => {
		if (tableId && typeof window !== "undefined") {
			try {
				localStorage.removeItem(storageKey(tableId, "groupBy"));
			} catch {}
		}
		return [];
	});
	const activeGroupBy = externalGroupBy ?? internalGroupBy;
	const [schemes, setSchemes] = React.useState<TableScheme[]>(() =>
		normalizeSchemes(loadFromStorage<unknown>(tableId, "schemes", [])),
	);
	const [activeSchemeId, setActiveSchemeId] = React.useState<string | null>(() =>
		loadFromStorage(tableId, "activeSchemeId", null),
	);
	const [columnColors, setColumnColors] = React.useState<ColumnColors>(() => {
		const saved = parseColumnColors(loadFromStorage<unknown>(tableId, "columnColors", null));
		if (saved) return saved;
		const legacyColumns = loadFromStorage<unknown>(tableId, "coloredColumns", []);
		const legacyColor = normalizeColumnColor(
			loadFromStorage<unknown>(tableId, "columnColor", null),
		);
		return Array.isArray(legacyColumns)
			? Object.fromEntries(
					legacyColumns
						.filter((id): id is string => typeof id === "string")
						.map((id) => [id, legacyColor]),
				)
			: {};
	});

	// Grouping: pre-aggregate rows by selected text columns
	const processedData = React.useMemo<TData[]>(() => {
		if (serverGrouped || activeGroupBy.length === 0) return data;

		const groups = new Map<string, TData[]>();
		for (const row of data) {
			const key = activeGroupBy
				.map((colId) => String((row as Record<string, unknown>)[colId] ?? ""))
				.join("\x00");
			if (!groups.has(key)) groups.set(key, []);
			groups.get(key)?.push(row);
		}

		return Array.from(groups.entries()).map(([key, rows]) => {
			const keyParts = key.split("\x00");
			const firstRow = rows[0] as Record<string, unknown>;
			const result: Record<string, unknown> = {};

			for (let i = 0; i < activeGroupBy.length; i++) {
				result[activeGroupBy[i]] = keyParts[i];
				// Carry over label column(s) for this group key.
				// Wsparcie string ORAZ string[] — KSeF mapuje 1:1 (issuerNip→issuerName),
				// raporty typu DaneTab potrzebuja przepisac wiecej kolumn (nazwa, rodzaj,
				// KSID, apteka, fRx, fRecepta) ktore zawsze sa stale wewnatrz grupy.
				const labelCol = groupLabelColumns[activeGroupBy[i]];
				if (typeof labelCol === "string") {
					result[labelCol] = firstRow[labelCol];
				} else if (Array.isArray(labelCol)) {
					for (const c of labelCol) result[c] = firstRow[c];
				}
			}

			for (const colId of summableColumns) {
				if (activeGroupBy.includes(colId)) continue;
				// `toNumeric` akceptuje number ORAZ numeric string (rust_decimal "12.34")
				// — bez tego sumy walut/marz/ilosc kolapsowaly do 0 dla typowych
				// payloadow backendu (Rust Decimal → string przez serde).
				const values: number[] = [];
				for (const r of rows) {
					const n = toNumeric((r as Record<string, unknown>)[colId]);
					if (n !== null) values.push(n);
				}
				result[colId] = computeAggValue(values, aggregationConfig);
			}

			result.__rowCount__ = rows.length;
			return result as TData;
		});
	}, [data, activeGroupBy, summableColumns, aggregationConfig, groupLabelColumns, serverGrouped]);

	// Callbacks
	const clearAllFilters = React.useCallback(() => setColumnFilters([]), [setColumnFilters]);
	const setColumnFilter = React.useCallback(
		(columnId: string, value: string) => {
			setColumnFilters((prev) => {
				const exists = prev.some((f) => f.id === columnId);
				if (exists) {
					return prev.map((f) => (f.id === columnId ? { ...f, value } : f));
				}
				return [...prev, { id: columnId, value }];
			});
		},
		[setColumnFilters],
	);
	const handleRowClick = React.useCallback((rowId: string) => {
		setSelectedRowId((prev) => (prev === rowId ? null : rowId));
	}, []);
	// Telefon: stukniecie wiersza (w obu trybach) zaznacza go i otwiera pelny
	// ekran szczegolow. W trybie „Tabela” klik w kontrolke komorki zostaje jej.
	const openMobileDetails = React.useCallback((rowId: string) => {
		setSelectedRowId(rowId);
		setMobileDetailsRowId(rowId);
	}, []);
	const handleMobileTableRowClick = React.useCallback(
		(rowId: string, event?: React.MouseEvent<HTMLElement>) => {
			if (isCellControlClick(event)) return;
			openMobileDetails(rowId);
		},
		[openMobileDetails],
	);
	// Wiersz otwiera widok strony (`onRowClick`) — zaznaczenie zostaje jako
	// podswietlenie ostatnio otwartego wiersza, bez przelaczania przy drugim kliku.
	const handleExternalRowClick = React.useCallback(
		(rowId: string, event?: React.MouseEvent<HTMLElement>) => {
			if (isCellControlClick(event)) return;
			setSelectedRowId(rowId);
			onRowClick?.(rowId);
		},
		[onRowClick],
	);
	const rowClickHandler = onRowClick
		? handleExternalRowClick
		: isMobileTable
			? handleMobileTableRowClick
			: handleRowClick;
	const scrollToTop = React.useCallback(() => {
		tableContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
	}, []);

	React.useEffect(() => {
		onClearFiltersReady?.(clearAllFilters);
	}, [onClearFiltersReady, clearAllFilters]);
	React.useEffect(() => {
		onSetColumnFilterReady?.(setColumnFilter);
	}, [onSetColumnFilterReady, setColumnFilter]);
	React.useEffect(() => {
		onSelectedRowChange?.(selectedRowId !== null);
	}, [onSelectedRowChange, selectedRowId]);

	React.useEffect(() => {
		if (filterResetKey !== undefined) setColumnFilters([]);
	}, [filterResetKey, setColumnFilters]);

	// Persistence
	React.useEffect(() => {
		saveToStorage(tableId, "columnOrder", columnOrder);
	}, [tableId, columnOrder]);
	React.useEffect(() => {
		saveToStorage(tableId, "columnPinning", columnPinning);
	}, [tableId, columnPinning]);
	React.useEffect(() => {
		saveToStorage(tableId, "columnVisibility", columnVisibility);
	}, [tableId, columnVisibility]);
	React.useEffect(() => {
		saveToStorage(tableId, "columnSizing", columnSizing);
	}, [tableId, columnSizing]);
	React.useEffect(() => {
		saveToStorage(tableId, "aggregationConfig", aggregationConfig);
	}, [tableId, aggregationConfig]);
	// groupBy intentionally NOT persisted to localStorage — prevents freeze on reload with large datasets
	React.useEffect(() => {
		saveToStorage(tableId, "schemes", schemes);
	}, [tableId, schemes]);
	React.useEffect(() => {
		saveToStorage(tableId, "activeSchemeId", activeSchemeId);
	}, [tableId, activeSchemeId]);
	React.useEffect(() => {
		saveToStorage(tableId, "columnColors", columnColors);
	}, [tableId, columnColors]);
	React.useEffect(() => {
		if (mobileMeasureId) saveToStorage(tableId, "mobileMeasure", mobileMeasureId);
	}, [tableId, mobileMeasureId]);

	// Scheme management
	const handleApplyDefaultScheme = React.useCallback(() => {
		setColumnOrder(defaultColumnOrder);
		setColumnPinning({ start: defaultPinnedColumns, end: [] });
		setColumnVisibility(defaultColumnVisibility ?? {});
		setColumnSizing({});
		setActiveSchemeId("default");
		if (onGroupByChangeProp) {
			onGroupByChangeProp([]);
		} else {
			setInternalGroupBy([]);
		}
	}, [
		defaultColumnOrder,
		defaultPinnedColumns,
		defaultColumnVisibility,
		onGroupByChangeProp,
		setColumnVisibility,
	]);

	const handleApplyScheme = React.useCallback(
		(scheme: TableScheme) => {
			setColumnOrder(scheme.columnOrder);
			setColumnPinning(normalizeColumnPinning(scheme.columnPinning, { start: [], end: [] }));
			setColumnVisibility(scheme.columnVisibility);
			setColumnSizing(scheme.columnSizing ?? {});
			setActiveSchemeId(scheme.id);
			// Restore groupBy from scheme
			const savedGroupBy = scheme.groupBy ?? [];
			if (onGroupByChangeProp) {
				onGroupByChangeProp(savedGroupBy);
			} else {
				setInternalGroupBy(savedGroupBy);
			}
		},
		[onGroupByChangeProp, setColumnVisibility],
	);

	const handleSaveScheme = React.useCallback(
		(name: string) => {
			const newScheme: TableScheme = {
				id: Date.now().toString(),
				name,
				columnOrder,
				columnPinning,
				columnVisibility,
				columnSizing,
				groupBy: activeGroupBy.length > 0 ? activeGroupBy : undefined,
			};
			const updated = [...schemes, newScheme];
			setSchemes(updated);
			setActiveSchemeId(newScheme.id);
		},
		[columnOrder, columnPinning, columnVisibility, columnSizing, schemes, activeGroupBy],
	);

	const handleDeleteScheme = React.useCallback(
		(id: string) => {
			const updated = schemes.filter((s) => s.id !== id);
			setSchemes(updated);
			if (activeSchemeId === id) setActiveSchemeId(null);
		},
		[schemes, activeSchemeId],
	);

	// Grouping change via startTransition to avoid blocking the main thread
	const [, startGroupByTransition] = React.useTransition();
	const handleGroupByChange = React.useCallback(
		(ids: string[]) => {
			if (onGroupByChangeProp) {
				onGroupByChangeProp(ids);
				return;
			}
			if (ids.length > 0 && data.length > 5000) {
				// biome-ignore lint/suspicious/noAlert: Legacy dialog; replace with ConfirmDialog when changing this flow.
				const ok = window.confirm(
					`Grupowanie ${data.length.toLocaleString("pl-PL")} wierszy moze chwile potrwac. Kontynuowac?`,
				);
				if (!ok) return;
			}
			startGroupByTransition(() => setInternalGroupBy(ids));
		},
		[data.length, onGroupByChangeProp],
	);

	// DnD
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
	);

	void enableColumnOrdering;

	// Auto-assign filterFn + numeric zero-sort-last
	const numericSortFn = React.useCallback((rowA: Row<TData>, rowB: Row<TData>, colId: string) => {
		const a = (rowA.getValue<number>(colId) ?? 0) || 0;
		const b = (rowB.getValue<number>(colId) ?? 0) || 0;
		if (a === 0 && b === 0) return 0;
		if (a === 0 || b === 0) {
			const isDesc = sortingRef.current.find((s) => s.id === colId)?.desc ?? false;
			if (a === 0) return isDesc ? -1 : 1;
			return isDesc ? 1 : -1;
		}
		return a - b;
	}, []);
	const columnsWithFilterFn = React.useMemo<ColumnDef<TData, unknown>[]>(
		() =>
			columns.map((col) => {
				const filterFn = col.filterFn
					? undefined
					: col.meta?.facetedOptions
						? facetedFilter
						: col.meta?.isDate
							? dateRangeFilter
							: col.meta?.isNumeric
								? numberRangeFilter
								: textWithBangFilter;
				// Kolumny liczbowe: wlasny komparator (zera na koniec). Pozostale bez
				// `sortFn` dostaja `auto` z v8 — v9 probkuje inne wiersze i bez tego
				// zmienilby kolejnosc w UI i w eksporcie (data-table.sort-parity.test).
				const sortExtras =
					col.meta?.isNumeric && !col.sortFn
						? { sortFn: numericSortFn, sortUndefined: -1 as const }
						: !col.sortFn || col.sortFn === "auto"
							? { sortFn: legacyAutoSortFn }
							: {};
				return filterFn ? { ...col, filterFn, ...sortExtras } : { ...col, ...sortExtras };
			}),
		[columns, numericSortFn],
	);

	// TanStack Table v9. `useTable` zwraca nowy obiekt przy kazdym renderze
	// (spread instancji + state), wiec callbacki czytaja go przez ref —
	// inaczej `handleExport` zmienialby sie co render i `onExportReady`
	// rodzica, ktory trzyma funkcje w stanie, krecilby petle renderow.
	const table = useTable({
		features: dataTableFeatures,
		data: processedData,
		columns: columnsWithFilterFn,
		state: { sorting, columnFilters, columnOrder, columnPinning, columnVisibility, columnSizing },
		onSortingChange: handleSortingChange,
		onColumnFiltersChange: handleColumnFiltersChange,
		onColumnOrderChange: setColumnOrder,
		onColumnPinningChange: setColumnPinning,
		onColumnVisibilityChange: setColumnVisibility,
		onColumnSizingChange: setColumnSizing,
		enableColumnResizing: true,
		columnResizeMode: "onChange",
		enableColumnPinning,
		manualFiltering: serverFiltering,
		manualSorting: manualSortingProp ?? (!!onSortingChangeProp && !serverGrouped),
		...(getRowId ? { getRowId } : {}),
	});
	const tableRef = React.useRef(table);
	tableRef.current = table;

	// Notification of parent on filter/sorting changes is handled inline by
	// handleColumnFiltersChange / handleSortingChange (controlled-pair pattern).

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (over && active.id !== over.id) {
			// Use visual order (orderedColumns) as source of truth for DnD indices,
			// because SortableContext collision detection operates in visual space.
			// columnOrder (TanStack internal) may diverge from orderedColumns when
			// alwaysLast columns are involved, causing incorrect arrayMove indices.
			const visualIds = orderedColumns.map((c) => c.id);
			const oldIdx = visualIds.indexOf(active.id as string);
			const newIdx = visualIds.indexOf(over.id as string);
			if (oldIdx === -1 || newIdx === -1) return;
			setColumnOrder((prev) => {
				const hiddenCols = prev.filter((id) => !visualIds.includes(id));
				return [...arrayMove(visualIds, oldIdx, newIdx), ...hiddenCols];
			});
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: table is stable ref; column state changes cover recomputation
	const visibleColumns = React.useMemo(
		() => table.getVisibleLeafColumns(),
		[columnVisibility, columnOrder, columns],
	);
	// biome-ignore lint/correctness/useExhaustiveDependencies: table is stable ref; explicit deps cover all relevant state
	const filteredRows = React.useMemo(
		() => table.getSortedRowModel().rows,
		[processedData, sorting, columnFilters, columns],
	);
	React.useEffect(() => {
		onFilteredRowCountChange?.(serverFiltering ? data.length : filteredRows.length);
	}, [data.length, filteredRows.length, onFilteredRowCountChange, serverFiltering]);
	React.useEffect(() => {
		// Telefon: stukniecie otwiera wlasne szczegoly wiersza, a widok strony
		// (`onRowSelect`) uruchamia przycisk w ich stopce — inaczej otwieraly sie
		// dwie warstwy naraz, a ten sam wiersz nie dal sie otworzyc ponownie.
		if (!onRowSelect || isBelowMd) return;
		const original = selectedRowId
			? (filteredRows.find((r) => r.id === selectedRowId)?.original ?? null)
			: null;
		onRowSelect(original);
	}, [onRowSelect, selectedRowId, filteredRows, isBelowMd]);
	// Role kolumn dla telefonu (tytul / podtytul / miary / akcje) — automat z
	// `table-mobile.ts`, nadpisywany przez `meta.mobileRole`.
	const mobileRoles = React.useMemo(
		() =>
			resolveColumnRoles(
				visibleColumns.map((c) => ({
					id: c.id,
					role: c.columnDef.meta?.mobileRole,
					numeric: !!c.columnDef.meta?.isNumeric,
					date: !!c.columnDef.meta?.isDate,
					actions: isAlwaysLastColumn(c),
					additive: summableColumns.includes(c.id),
					defaultMeasure: c.columnDef.meta?.mobileDefaultMeasure,
				})),
			),
		[visibleColumns, summableColumns],
	);
	// Tryb „Tabela” na telefonie nie przypina kolumn: przy tej szerokosci ekranu
	// przypieta kolumna zabiera miejsce reszcie. Zapisane przypiecia desktopu
	// zostaja w stanie i wracaja od `md` w gore.
	const pinnedColumnIds = React.useMemo(
		() => (isMobileTable ? [] : columnPinning.start),
		[isMobileTable, columnPinning.start],
	);

	const getCellColorBg = React.useCallback(
		(columnId: string, _value: unknown): React.CSSProperties | undefined => {
			const columnColor = columnColors[columnId];
			return columnColor ? buildColumnColorStyle(columnColor) : undefined;
		},
		[columnColors],
	);

	const scrollToSelected = React.useCallback(() => {
		if (!selectedRowId) return;
		const idx = filteredRows.findIndex((r) => r.id === selectedRowId);
		if (idx === -1) return;
		tableContainerRef.current?.scrollTo({ top: idx * rowHeight, behavior: "smooth" });
	}, [selectedRowId, filteredRows, rowHeight]);

	const orderedColumns = React.useMemo(() => {
		const pinned = pinnedColumnIds
			.map((id) => visibleColumns.find((c) => c.id === id))
			.filter(Boolean) as typeof visibleColumns;
		const unpinned = visibleColumns.filter((c) => !pinnedColumnIds.includes(c.id));
		const normal = unpinned.filter((c) => !isAlwaysLastColumn(c));
		const last = unpinned.filter((c) => isAlwaysLastColumn(c));
		return [...pinned, ...normal, ...last];
	}, [visibleColumns, pinnedColumnIds]);

	// Memoize column ID array for SortableContext — passing inline .map() creates a new array
	// reference every render, causing SortableContext to re-render ALL useSortable consumers
	// (even if column order didn't change), which triggers focus loss on filter inputs.
	// alwaysLast and pinned columns are excluded so they cannot be dragged at all
	// (matching DragGrip auto-disable in DataTableColumnHeader). Pinned cells remain
	// sticky and are not wrapped in SortableDataCell to avoid sticky+transform conflicts.
	const sortableColumnIds = React.useMemo(
		() =>
			orderedColumns
				.filter((c) => !isAlwaysLastColumn(c) && !pinnedColumnIds.includes(c.id))
				.map((c) => c.id),
		[orderedColumns, pinnedColumnIds],
	);

	const pinnedOffsets = React.useMemo(() => {
		const offsets: Record<string, number> = {};
		let cumulative = 0;
		for (const id of pinnedColumnIds) {
			offsets[id] = cumulative;
			cumulative += visibleColumns.find((c) => c.id === id)?.getSize() ?? 150;
		}
		return offsets;
	}, [pinnedColumnIds, visibleColumns]);

	const isColumnPinned = React.useCallback(
		(id: string) => pinnedColumnIds.includes(id),
		[pinnedColumnIds],
	);
	const isLastPinnedColumn = React.useCallback(
		(id: string) => pinnedColumnIds.indexOf(id) === pinnedColumnIds.length - 1,
		[pinnedColumnIds],
	);
	const getLeftOffset = React.useCallback((id: string) => pinnedOffsets[id] ?? 0, [pinnedOffsets]);
	const getPinnedZIndex = React.useCallback(
		(id: string, base: number) => {
			const idx = pinnedColumnIds.indexOf(id);
			return idx < 0 ? undefined : base + (pinnedColumnIds.length - idx);
		},
		[pinnedColumnIds],
	);

	const pinnedSeparatorClass = "shadow-[inset_-1px_0_0_0_var(--color-border)]";
	const lastPinnedShadowClass =
		"shadow-[inset_-1px_0_0_0_var(--color-border),3px_0_6px_-2px_rgba(0,0,0,0.15)]";

	const columnSizingKey = React.useMemo(() => JSON.stringify(columnSizing), [columnSizing]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: columnSizing needed — col.getSize() reads it implicitly
	const totalColumnsWidth = React.useMemo(
		() => orderedColumns.reduce((sum, col) => sum + col.getSize(), 0),
		[orderedColumns, columnSizing],
	);

	const tableContainerRef = React.useRef<HTMLDivElement>(null);
	const infiniteScrollSentinelRef = React.useRef<HTMLDivElement>(null);
	const headerContainerRef = React.useRef<HTMLDivElement>(null);
	const footerContainerRef = React.useRef<HTMLDivElement>(null);
	const [headerHeight, setHeaderHeight] = React.useState(0);
	const [footerHeight, setFooterHeight] = React.useState(0);
	const [horizontalScrollbarHeight, setHorizontalScrollbarHeight] = React.useState(0);
	const shouldVirtualize = filteredRows.length > virtualizationThreshold;

	// Telefon w trybie „Lista” nie renderuje kontenera tabeli — po przelaczeniu
	// na „Tabela” pomiar musi ruszyc od nowa, stad zaleznosc od trybu.
	// biome-ignore lint/correctness/useExhaustiveDependencies: isMobileList decyduje, czy refy istnieja
	React.useLayoutEffect(() => {
		const container = tableContainerRef.current;
		const header = headerContainerRef.current;
		const footer = footerContainerRef.current;
		if (!container || !header || !footer) return;
		const measure = () => {
			setHeaderHeight(header.offsetHeight);
			setFooterHeight(footer.offsetHeight);
			setHorizontalScrollbarHeight(container.offsetHeight - container.clientHeight);
		};
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(container);
		observer.observe(header);
		observer.observe(footer);
		return () => observer.disconnect();
	}, [isMobileList]);

	const summaryQuickViewRef = React.useRef<HTMLDivElement>(null);
	const orderedColumnsRef = React.useRef(orderedColumns);
	orderedColumnsRef.current = orderedColumns;
	const quickViewColIdRef = React.useRef<string | undefined>(undefined);
	quickViewColIdRef.current = summaryQuickViewHideAt ?? summaryQuickView?.[0]?.columnId;

	const handleTableScroll = React.useCallback(() => {
		const el = tableContainerRef.current;
		if (!el) return;
		const sl = el.scrollLeft;
		const st = el.scrollTop;
		// Hide quick-view overlay when target column scrolls into view
		const qvEl = summaryQuickViewRef.current;
		const qvColId = quickViewColIdRef.current;
		if (qvEl && qvColId) {
			let colRight = 0;
			for (const c of orderedColumnsRef.current) {
				colRight += c.getSize();
				if (c.id === qvColId) break;
			}
			const isVisible = sl + el.clientWidth >= colRight;
			qvEl.style.opacity = isVisible ? "0" : "1";
			qvEl.style.pointerEvents = isVisible ? "none" : "auto";
		}
		setShowScrollTop(st > 150);
		onScroll?.(st);
	}, [onScroll]);

	// Infinite scroll przez IntersectionObserver — prefetch zanim user dojedzie do dolu.
	// rootMargin 1500px = traktuj 1500px ponizej viewportu jako "widoczne", czyli fetch
	// startuje gdy sentinel jest 1500px od dna scrollHeight. Wylaczone przy grouping —
	// grouping re-liczy sie na kazdej nowej stronie, grupy znikaja/pojawiaja sie podczas
	// scrolla.
	//
	// Safeguard: gdy `serverFiltering=false` ALE user wlaczyl client-side column filter,
	// `filteredRows.length` moze byc znaczaco mniejsze od `data.length` → sentinel stale
	// w viewporcie → auto-fetch loop'uje po 500 wierszy az do exhaust pages. Zwroty
	// kwartalne na produkcji manifestowaly to przy filtrze KSID po bloz=X. Backend filtr
	// (gdy istnieje) jest preferowany — auto-fetch przy aktywnym client filtrze off.
	const clientColumnFilterActive = !serverFiltering && columnFilters.length > 0;
	// biome-ignore lint/correctness/useExhaustiveDependencies: isMobileList decyduje, czy sentinel istnieje (lista ma wlasny)
	React.useEffect(() => {
		const root = tableContainerRef.current;
		const sentinel = infiniteScrollSentinelRef.current;
		if (
			!root ||
			!sentinel ||
			!onFetchMore ||
			!hasNextPage ||
			activeGroupBy.length > 0 ||
			serverGrouped ||
			clientColumnFilterActive
		)
			return;
		const io = new IntersectionObserver(
			([entry]) => {
				if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
					onFetchMore();
				}
			},
			{ root, rootMargin: "0px 0px 1500px 0px" },
		);
		io.observe(sentinel);
		return () => io.disconnect();
	}, [
		onFetchMore,
		hasNextPage,
		isFetchingNextPage,
		activeGroupBy,
		serverGrouped,
		clientColumnFilterActive,
		isMobileList,
	]);

	// --- Telefon: tryb „Lista”, arkusze i ekran szczegolow ----------------------
	const mobileColumnLabel = (column: Column<TData, unknown>): string => {
		const header = column.columnDef.header;
		return (
			column.columnDef.meta?.mobileLabel ??
			columnLabels?.[column.id] ??
			(typeof header === "string" ? header : headerElementProp(column, "title")) ??
			column.id
		);
	};
	/** Opis kolumny z `<DataTableColumnHeader helpText=…>` — na telefonie w menu i szczegolach. */
	const mobileColumnHelp = (column: Column<TData, unknown>) =>
		headerElementProp(column, "helpText");
	// Naglowek funkcyjny (`<DataTableColumnHeader title=…>`) bez `columnLabels`:
	// tytul czytamy z propsow zwroconego elementu, zeby lista i szczegoly nie
	// pokazywaly surowych identyfikatorow kolumn. Naglowki nie uzywaja hookow.
	const headerElementProp = (
		column: Column<TData, unknown>,
		prop: "title" | "helpText",
	): string | undefined => {
		const header = column.columnDef.header;
		if (typeof header !== "function") return undefined;
		const context = table
			.getFlatHeaders()
			.find((h) => h.column.id === column.id)
			?.getContext();
		if (!context) return undefined;
		try {
			const element = header(context);
			if (!React.isValidElement(element)) return undefined;
			const value = (element.props as Record<string, unknown>)[prop];
			return typeof value === "string" ? value : undefined;
		} catch {
			return undefined;
		}
	};
	const renderMobileCell = (row: Row<TData>, columnId: string | null): React.ReactNode => {
		if (!columnId) return null;
		const cell = row.getVisibleCells().find((c) => c.column.id === columnId);
		return cell ? flexRender(cell.column.columnDef.cell, cell.getContext()) : null;
	};
	const renderMobileSubtitle = (row: Row<TData>): React.ReactNode =>
		mobileRoles.subtitleIds.length === 0
			? undefined
			: mobileRoles.subtitleIds.map((id, i) => (
					<React.Fragment key={id}>
						{i > 0 && " · "}
						{renderMobileCell(row, id)}
					</React.Fragment>
				));
	const mobileMeasureColumnId =
		mobileMeasureId && mobileRoles.measureIds.includes(mobileMeasureId)
			? mobileMeasureId
			: mobileRoles.defaultMeasureId;
	const mobileMeasureColumn = mobileMeasureColumnId
		? visibleColumns.find((c) => c.id === mobileMeasureColumnId)
		: undefined;
	const mobileMeasureMax = React.useMemo(() => {
		if (!isMobileList || !mobileMeasureColumnId) return 0;
		let max = 0;
		for (const r of filteredRows) {
			const n = toNumeric(r.getValue(mobileMeasureColumnId));
			if (n !== null && Math.abs(n) > max) max = Math.abs(n);
		}
		return max;
	}, [isMobileList, mobileMeasureColumnId, filteredRows]);
	const mobileShownCount = serverFiltering ? data.length : filteredRows.length;
	const mobileRowsLabel = `${mobileShownCount.toLocaleString("pl-PL")}${
		totalCount != null && totalCount > mobileShownCount
			? ` z ${totalCount.toLocaleString("pl-PL")}`
			: ""
	} wierszy`;
	const mobileAggregationLabel = aggregationConfig.useMedian
		? "Mediana"
		: aggregationConfig.useAverage
			? "Średnia"
			: "Suma";
	const mobileInfiniteScroll =
		!!onFetchMore && activeGroupBy.length === 0 && !serverGrouped && !clientColumnFilterActive;
	const mobileFilterableColumns = isBelowMd
		? visibleColumns.filter(
				(c) => !isAlwaysLastColumn(c) && (c.getCanFilter() || !!c.columnDef.meta?.filterRender),
			)
		: [];
	const mobileDetailsRow =
		isBelowMd && mobileDetailsRowId
			? (filteredRows.find((r) => r.id === mobileDetailsRowId) ?? null)
			: null;
	const mobileFilterColumn =
		isMobileTable && mobileFilterColumnId
			? (visibleColumns.find((c) => c.id === mobileFilterColumnId) ?? null)
			: null;
	const mobileContextValue = React.useMemo(
		() => ({ compactToolbar: isBelowMd, mobileHeader: isMobileTable }),
		[isBelowMd, isMobileTable],
	);

	const rowVirtualizer = useVirtualizer({
		count: filteredRows.length,
		getScrollElement: () => tableContainerRef.current,
		estimateSize: () => rowHeight,
		scrollMargin: headerHeight,
		overscan: 10,
	});

	const summaryLabelColId = React.useMemo(
		() => (pinnedColumnIds.length > 0 ? pinnedColumnIds[0] : visibleColumns[0]?.id),
		[pinnedColumnIds, visibleColumns],
	);

	const rawSummaryValues = React.useMemo<Record<string, number | null>>(() => {
		if (!showSummaryRow || summableColumns.length === 0) return {};
		const valsByCol: Record<string, number[]> = {};
		for (const id of summableColumns) valsByCol[id] = [];
		for (const row of filteredRows) {
			for (const id of summableColumns) {
				const n = toNumeric(row.getValue(id));
				if (n !== null) valsByCol[id].push(n);
			}
		}
		const result: Record<string, number | null> = {};
		for (const id of summableColumns) {
			result[id] = computeAggValue(valsByCol[id], aggregationConfig);
		}
		// Match the visible summary: server totals cover all filtered pages.
		// Formatted strings/JSX are display-only; custom average/median still
		// use the selected aggregation instead of a server sum.
		const isDefaultSum = !aggregationConfig.useAverage && !aggregationConfig.useMedian;
		for (const [key, value] of Object.entries(summaryData ?? {})) {
			if (
				(typeof value === "number" || value === null) &&
				((isDefaultSum && summableColumns.includes(key)) ||
					(!summableColumns.includes(key) && key !== summaryLabelColId))
			) {
				result[key] = value;
			}
		}
		return result;
	}, [
		filteredRows,
		showSummaryRow,
		summableColumns,
		aggregationConfig,
		summaryData,
		summaryLabelColId,
	]);

	const [isExportingInternal, setIsExportingInternal] = React.useState(false);

	const handleExport = React.useCallback(async () => {
		const table = tableRef.current;
		try {
			setIsExportingInternal(true);
			onExportingChange?.(true);

			// ── Server-side export path ──────────────────────────────────────
			if (exportEndpoint) {
				const finishExport = beginDataExport();
				const { getAccessToken, getTenantOverride } = await import("@/lib/auth-api");
				const { buildExportColumnOrder } = await import("@/lib/export-column-order");

				// Issue #54: kolejnosc kolumn z orderedColumns (visual order)
				// zeby drag&drop w UI byl odzwierciedlony w pliku eksportu.
				const allCols = buildExportColumnOrder(orderedColumns, alwaysExportColumns ?? [], (id) =>
					table.getColumn(id),
				);

				const columns = allCols.map((col) => ({
					id: col.id,
					header: (columnLabels?.[col.id] ?? col.id) as string,
					isNumeric: !!(col.columnDef.meta as { isNumeric?: boolean } | undefined)?.isNumeric,
					widthPx: col.getSize(),
				}));

				const summaryMode = aggregationConfig.useMedian
					? "median"
					: aggregationConfig.useAverage
						? "avg"
						: "sum";

				const d = new Date();
				const pad = (n: number) => String(n).padStart(2, "0");
				const serverTs = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}-${pad(d.getMinutes())}`;

				const body = {
					filters: exportFilters ?? {},
					columns,
					summaryColumns: summableColumns ?? [],
					summaryMode,
					fileName: `${exportFileName ?? tableId ?? "eksport"}_${serverTs}`,
				};

				const protocol = window.location.protocol === "https:" ? "https:" : "http:";
				const host = window.location.hostname || "localhost";
				const baseUrl = import.meta.env.VITE_API_URL ?? `${protocol}//${host}:3000/api`;

				const headers: Record<string, string> = {
					"Content-Type": "application/json",
					"X-Requested-With": "XMLHttpRequest",
				};
				const token = getAccessToken();
				if (token) headers.Authorization = `Bearer ${token}`;
				const tenantOverride = getTenantOverride();
				if (tenantOverride !== null) {
					headers["X-Tenant-Override"] = String(tenantOverride);
				}

				const response = await fetch(`${baseUrl}${exportEndpoint}`, {
					method: "POST",
					credentials: "include",
					headers,
					body: JSON.stringify(body),
				});

				if (!response.ok) {
					const errBody = await response.json().catch(() => ({ error: "Unknown error" }));
					throw new Error(errBody.error ?? `Export failed: ${response.status}`);
				}

				// Backend sygnalizuje ucieciem wyniku przez `x-export-truncated`
				// (`reports/expiry_batches.rs`). Wczesniej front ten naglowek wyrzucal
				// i zapisywal niepelny plik bez slowa.
				if (response.headers.get("x-export-truncated") === "true") {
					const rows = response.headers.get("x-export-rows");
					throw new Error(
						`Eksport przerwany: serwer ucial wynik${rows ? ` na ${Number(rows).toLocaleString("pl-PL")} wierszach` : ""}. Plik nie zostal zapisany, bo bylby niepelny. Zawez zakres (filtry, krotszy okres) i sprobuj ponownie.`,
					);
				}

				const blob = await response.blob();
				const url = URL.createObjectURL(blob);
				const a = document.createElement("a");
				a.href = url;
				const disposition = response.headers.get("Content-Disposition");
				const match = disposition?.match(/filename="?(.+?)"?$/);
				a.download = match?.[1] ?? `${exportFileName ?? "eksport"}_${serverTs}.xlsx`;
				document.body.appendChild(a);
				a.click();
				document.body.removeChild(a);
				setTimeout(() => URL.revokeObjectURL(url), 60_000);
				finishExport();
				return;
			}

			// ── Client-side export path (fallback) ──────────────────────────
			// Issue #54: eksport musi respektowac user-ustawiona kolejnosc kolumn
			// (drag&drop) — uzywamy `orderedColumns` (visual order: pinned +
			// user-reordered + alwaysLast) zamiast sortu po defaultColumnOrder.
			const { exportToExcel, ExcelExportError } = await import("@/lib/export-excel");
			const { buildExportColumnOrder } = await import("@/lib/export-column-order");
			const visibleCols = table.getVisibleLeafColumns();
			const mapCol = (col: (typeof visibleCols)[number]) => ({
				id: col.id,
				header: (columnLabels?.[col.id] ?? col.id) as string,
				widthPx: col.getSize(),
				isNumeric: !!col.columnDef.meta?.isNumeric,
				percent: !!col.columnDef.meta?.isPercent,
			});
			const allExportCols = buildExportColumnOrder(
				orderedColumns,
				alwaysExportColumns ?? [],
				(id) => table.getColumn(id),
			);
			const excelColumns = allExportCols.map(mapCol);

			// Straznik kompletnosci. Plik, ktory wyglada na kompletny, a nim nie jest,
			// jest gorszy niz brak pliku: nikt nie audytuje liczby, ktora wyglada
			// wiarygodnie. Raport produktowy gubil tak 8 773 z 58 773 pozycji przy
			// backendowym clampie 50 000. Zamiast zapisac to, co przyszlo, odmawiamy.
			// `expected` z tej samej odpowiedzi co wiersze jest scisly, wiec
			// sprawdzamy rowna liczbe (lapie tez DUPLIKATY z petli po OFFSET).
			// `totalCount` z propa przychodzi z innego zadania, wiec tam tylko niedobor.
			const assertComplete = (rows: number, expected: number, exact: boolean) => {
				if (exact ? rows === expected : rows >= expected) return;
				throw new ExcelExportError(
					`Eksport przerwany: pobrano ${rows.toLocaleString("pl-PL")} wierszy, a raport ma ${expected.toLocaleString("pl-PL")}. Plik nie zostal zapisany, bo bylby niepelny. Zawez zakres (filtry, krotszy okres) i sprobuj ponownie.`,
				);
			};

			let excelRows: Record<string, unknown>[];
			let exportSummary: Record<string, number | null> | undefined;
			if (fetchAllForExport) {
				const fetched = await fetchAllForExport();
				exportSummary = Array.isArray(fetched) ? undefined : fetched.summary;
				const authoritativeTotal = Array.isArray(fetched) ? undefined : fetched.totalCount;
				excelRows = Array.isArray(fetched) ? fetched : fetched.rows;
				// `serverGrouped` wyjete: tam `totalCount` liczy wiersze zrodlowe,
				// a eksport oddaje grupy, wiec mniej wierszy jest poprawnym wynikiem.
				if (!serverGrouped) {
					if (typeof authoritativeTotal === "number") {
						assertComplete(excelRows.length, authoritativeTotal, true);
					} else if (typeof totalCount === "number") {
						assertComplete(excelRows.length, totalCount, false);
					}
				}
			} else {
				excelRows = filteredRows.map((row) => {
					const obj: Record<string, unknown> = {};
					for (const col of allExportCols) {
						obj[col.id] = row.getValue(col.id);
					}
					return obj;
				});
				// Tabela ze stronicowaniem serwerowym (`onFetchMore`) trzyma w pamieci
				// tylko doladowane strony, wiec eksport z pamieci oddaje wycinek —
				// ten sam cichy defekt, tylko bez `fetchAllForExport`
				// (`features/reports/purchase-price-deviations-page.tsx`).
				// Grupowanie i kliencki filtr kolumnowy zawezaja wynik celowo,
				// wiec tam straznika nie ma.
				if (
					onFetchMore &&
					!serverGrouped &&
					activeGroupBy.length === 0 &&
					!clientColumnFilterActive &&
					typeof totalCount === "number"
				) {
					assertComplete(excelRows.length, totalCount, false);
				}
			}
			const hasSummary = showSummaryRow && summableColumns.length > 0;
			const summaryRowData = hasSummary ? { ...rawSummaryValues } : null;
			const isDefaultSum = !aggregationConfig.useAverage && !aggregationConfig.useMedian;
			if (summaryRowData && fetchAllForExport && !isDefaultSum) {
				for (const id of summableColumns) {
					const values = excelRows
						.map((row) => toNumeric(row[id]))
						.filter((value): value is number => value !== null);
					summaryRowData[id] = computeAggValue(values, aggregationConfig);
				}
			}
			// A fresh export response is authoritative for its rows and totals;
			// the screen may still hold the previous snapshot in Query's cache.
			if (summaryRowData && exportSummary) {
				for (const [key, value] of Object.entries(exportSummary)) {
					if (
						(isDefaultSum && summableColumns.includes(key)) ||
						(!summableColumns.includes(key) && key !== summaryLabelColId)
					)
						summaryRowData[key] = value;
				}
			}
			const summaryLabelText = aggregationConfig.useMedian
				? aggregationConfig.medianExcludeZero
					? "MED.!0"
					: "MED."
				: aggregationConfig.useAverage
					? "SRED."
					: summaryLabel;
			const d = new Date();
			const pad = (n: number) => String(n).padStart(2, "0");
			const ts = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}-${pad(d.getMinutes())}`;
			if (exportAdditionalRows?.length) excelRows = [...excelRows, ...exportAdditionalRows];
			await exportToExcel({
				columns: excelColumns,
				rows: excelRows,
				summaryRow: summaryRowData,
				summaryLabel: summaryLabelText,
				summaryLabelColumnId: summaryLabelColId,
				fileName: `${exportFileName ?? tableId ?? "eksport"}_${ts}`,
			});
		} catch (err) {
			// If the export chunk (`@/lib/export-excel`, `@/lib/export-column-order`)
			// failed to load because a Vercel deploy invalidated asset hashes
			// mid-session, schedule a one-shot reload — same recovery path as
			// the modal upload flows in features/contracts/.
			attemptDynamicImportRecovery(err);
			console.error("[DataTable] Export failed:", err);
			const error = err instanceof Error ? err : new Error("Eksport nie powiodl sie");
			// Strona, ktora nie podala `onExportError`, milczalaby na ekranie —
			// a nieudany eksport (np. odmowa zapisu niepelnego pliku powyzej)
			// musi byc widoczny, inaczej brak pliku wyglada jak zawieszenie.
			// Toast to istniejacy kanal, patrz WynikiTopPage.
			if (onExportError) onExportError(error);
			else toast.error(error.message);
		} finally {
			setIsExportingInternal(false);
			onExportingChange?.(false);
		}
	}, [
		filteredRows,
		columnLabels,
		showSummaryRow,
		summableColumns,
		rawSummaryValues,
		aggregationConfig,
		summaryLabel,
		summaryLabelColId,
		exportFileName,
		tableId,
		onExportError,
		fetchAllForExport,
		exportAdditionalRows,
		onExportingChange,
		alwaysExportColumns,
		exportEndpoint,
		exportFilters,
		orderedColumns,
		totalCount,
		serverGrouped,
		onFetchMore,
		activeGroupBy,
		clientColumnFilterActive,
	]);
	React.useEffect(() => {
		onExportReady?.(handleExport);
	}, [onExportReady, handleExport]);
	// Summary row — respects aggregationConfig
	const dynamicSummary = React.useMemo<Record<string, React.ReactNode>>(() => {
		if (!showSummaryRow && summableColumns.length === 0 && !summaryData && !deriveSummary)
			return {};

		const valsByCol: Record<string, number[]> = {};
		for (const id of summableColumns) valsByCol[id] = [];

		for (const row of filteredRows) {
			for (const id of summableColumns) {
				const n = toNumeric(row.getValue(id));
				if (n !== null) {
					valsByCol[id].push(n);
				}
			}
		}

		const formatted: Record<string, React.ReactNode> = {};

		if (summaryLabelColId && !summableColumns.includes(summaryLabelColId)) {
			const label = aggregationConfig.useMedian
				? aggregationConfig.medianExcludeZero
					? "MED.!0"
					: "MED."
				: aggregationConfig.useAverage
					? "SRED."
					: summaryLabel;
			formatted[summaryLabelColId] = (
				<span className="text-xs font-bold tracking-wide text-muted-foreground">{label}</span>
			);
		}

		for (const id of summableColumns) {
			const agg = computeAggValue(valsByCol[id], aggregationConfig);
			formatted[id] = summaryFormatter
				? summaryFormatter(id, agg)
				: agg.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
		}

		// deriveSummary: compute derived cells (e.g. weighted averages) for
		// columns outside summableColumns. Uses arithmetic sums regardless
		// of aggregationConfig.useMedian so weighted-average formulas stay
		// meaningful (median basket / median patients would be misleading).
		//
		// PRECEDENCE: deriveSummary runs BEFORE summaryData loop. If both
		// provide a value for the same column, summaryData wins (explicit
		// server-authoritative value overrides locally computed fallback).
		// See the summaryData loop below — its shouldOverride branch for
		// non-summable columns no longer gates on formatted[key] === undefined.
		if (showSummaryRow && deriveSummary) {
			const arithmeticSums: Record<string, number> = {};
			for (const id of summableColumns) {
				arithmeticSums[id] = valsByCol[id].reduce((a, v) => a + v, 0);
			}
			const derived = deriveSummary({
				sums: arithmeticSums,
				rowCount: filteredRows.length,
				rows: filteredRows.map((r) => r.original),
			});
			for (const [key, value] of Object.entries(derived)) {
				if (summableColumns.includes(key)) continue; // do not overwrite aggregated sums
				formatted[key] = value;
			}
		}

		// summaryData overrides client-side aggregation for default sum mode (server knows full dataset)
		const isDefaultSum = !aggregationConfig.useAverage && !aggregationConfig.useMedian;
		if (summaryData) {
			for (const [key, value] of Object.entries(summaryData)) {
				const shouldOverride =
					(isDefaultSum && summableColumns.includes(key)) ||
					(!summableColumns.includes(key) && key !== summaryLabelColId);
				if (shouldOverride) {
					// Raw numbers from server (e.g. {gross: 650.5000000000001}) must
					// be run through summaryFormatter — otherwise IEEE-754 floats
					// leak into the summary row. Already-formatted strings/JSX pass through.
					if (typeof value === "number" && summaryFormatter) {
						formatted[key] = summaryFormatter(key, value);
					} else {
						formatted[key] = value;
					}
				}
			}
		}

		return formatted;
	}, [
		filteredRows,
		summableColumns,
		summaryFormatter,
		deriveSummary,
		summaryLabel,
		summaryData,
		showSummaryRow,
		summaryLabelColId,
		aggregationConfig,
	]);

	// Cell renderers
	const renderHeaderCell = (
		col: (typeof orderedColumns)[number],
		headerGroup: ReturnType<typeof table.getHeaderGroups>[number],
	) => {
		const header = headerGroup.headers.find((h) => h.column.id === col.id);
		if (!header) return null;

		const pinned = isColumnPinned(header.column.id);
		const lastPinned = isLastPinnedColumn(header.column.id);
		const left = getLeftOffset(header.column.id);
		const zIndex = getPinnedZIndex(header.column.id, 30);
		const canFilter = header.column.getCanFilter();

		const headerClassName = cn(
			"group/header relative shrink-0 self-stretch px-2 py-1.5 text-left overflow-hidden",
			!pinned && "border-r border-border",
			pinned && "sticky bg-muted",
			pinned && !lastPinned && pinnedSeparatorClass,
			pinned && lastPinned && lastPinnedShadowClass,
			!pinned && header.column.columnDef.meta?.cellClassName,
		);
		const headerStyle: React.CSSProperties = {
			width: col.getSize(),
			left: pinned ? left : undefined,
			zIndex,
			...getCellColorBg(header.column.id, undefined),
			flexShrink: 0,
			...(header.column.columnDef.meta?.fill && !pinned ? { flexGrow: 1 } : {}),
		};
		const headerInner = isMobileTable ? (
			<MobileColumnHeaderMenu
				column={header.column}
				label={mobileColumnLabel(header.column)}
				help={mobileColumnHelp(header.column)}
				title={
					header.isPlaceholder
						? null
						: (flexRender(header.column.columnDef.header, header.getContext()) ??
							mobileColumnLabel(header.column))
				}
				isNumeric={!!header.column.columnDef.meta?.isNumeric}
				onFilter={() => setMobileFilterColumnId(header.column.id)}
			/>
		) : (
			<>
				<div className="overflow-hidden">
					{header.isPlaceholder
						? null
						: flexRender(header.column.columnDef.header, header.getContext())}
					{(canFilter || header.column.columnDef.meta?.filterRender) && !header.isPlaceholder && (
						<ColumnFilterInput column={header.column} label={columnLabels?.[col.id] ?? col.id} />
					)}
				</div>
				{header.column.getCanResize() && (
					// biome-ignore lint/a11y/noStaticElementInteractions: column resize handle
					<div
						onMouseDown={header.getResizeHandler()}
						onTouchStart={header.getResizeHandler()}
						onDoubleClick={() => header.column.resetSize()}
						className={cn(
							"absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none",
							"opacity-0 group-hover/header:opacity-100 transition-opacity",
							"bg-primary/40 hover:bg-primary/70",
							header.column.getIsResizing() && "opacity-100 bg-primary",
						)}
					/>
				)}
			</>
		);

		if (pinned) {
			return (
				<div key={header.id} className={headerClassName} style={headerStyle}>
					{headerInner}
				</div>
			);
		}
		return (
			<SortableHeaderWrapper
				key={header.id}
				columnId={header.column.id}
				className={headerClassName}
				style={headerStyle}
			>
				{headerInner}
			</SortableHeaderWrapper>
		);
	};

	const renderDataCell = (
		col: (typeof orderedColumns)[number],
		row: Row<TData>,
		isEven: boolean,
		isSelected = false,
		rowTint?: string,
	) => {
		const cell = row.getVisibleCells().find((c) => c.column.id === col.id);
		if (!cell) return null;

		const pinned = isColumnPinned(cell.column.id);
		const lastPinned = isLastPinnedColumn(cell.column.id);
		const left = getLeftOffset(cell.column.id);
		const zIndex = getPinnedZIndex(cell.column.id, 20);
		// rowTint (z `rowClassName`) zastepuje zebre — inaczej tlo malowane na
		// komorce zakrywa podswietlenie ustawione na wierszu.
		const rowBg = isSelected ? "" : rowTint ? rowTint : isEven ? "bg-card" : "bg-secondary";
		const bgStyle =
			(pinned || isSelected) && !(rowTint && !isSelected)
				? {
						background: isSelected
							? "var(--color-summary-row)"
							: isEven
								? "var(--color-card)"
								: "var(--color-secondary)",
					}
				: {};
		const isNumeric = cell.column.columnDef.meta?.isNumeric;
		const cellHeatmapStyle = getCellColorBg(cell.column.id, cell.getValue());
		const cellClassName = cn(
			"flex shrink-0 items-center px-1.5 py-1 sm:px-2 sm:py-1.5 text-[11px] sm:text-sm text-foreground overflow-hidden",
			isNumeric && "justify-end",
			!pinned && "border-r border-border last:border-r-0",
			rowBg,
			// Po rowBg — tailwind-merge daje wygrana klasie z meta (np. bg-primary/5
			// wyróżnia kolumnę kluczową, zastępując zebrę w obrębie tej kolumny).
			!pinned && !isSelected && cell.column.columnDef.meta?.cellClassName,
			pinned && "sticky",
			pinned && !lastPinned && pinnedSeparatorClass,
			pinned && lastPinned && lastPinnedShadowClass,
			pinned && "group-hover:!bg-muted",
		);
		const cellStyle: React.CSSProperties = {
			width: col.getSize(),
			left: pinned ? left : undefined,
			zIndex,
			...bgStyle,
			...(!isSelected ? (cellHeatmapStyle ?? {}) : {}),
			flexShrink: 0,
			overflow: "hidden",
			...(cell.column.columnDef.meta?.fill && !pinned ? { flexGrow: 1 } : {}),
		};
		const content = (
			<span className="truncate">{flexRender(cell.column.columnDef.cell, cell.getContext())}</span>
		);

		if (pinned) {
			return (
				<div key={cell.id} className={cellClassName} style={cellStyle}>
					{content}
				</div>
			);
		}
		return (
			<SortableDataCell
				key={cell.id}
				columnId={cell.column.id}
				className={cellClassName}
				style={cellStyle}
			>
				{content}
			</SortableDataCell>
		);
	};

	const renderSummaryCell = (col: (typeof orderedColumns)[number]) => {
		const pinned = isColumnPinned(col.id);
		const lastPinned = isLastPinnedColumn(col.id);
		const left = getLeftOffset(col.id);
		const zIndex = pinned ? getPinnedZIndex(col.id, 10) : undefined;
		const isNumericCol = summableColumns.includes(col.id);

		return (
			<div
				key={col.id}
				data-testid={`summary-cell-${col.id}`}
				className={cn(
					"flex shrink-0 items-center px-2 py-2 overflow-hidden",
					!pinned && "border-r border-border last:border-r-0",
					!pinned && col.columnDef.meta?.cellClassName,
					pinned && "sticky bg-muted",
					pinned && !lastPinned && pinnedSeparatorClass,
					pinned && lastPinned && lastPinnedShadowClass,
					isNumericCol && "justify-end tabular-nums",
				)}
				style={{
					width: col.getSize(),
					left: pinned ? left : undefined,
					zIndex,
					...getCellColorBg(col.id, undefined),
					flexShrink: 0,
					// Jak naglowek i tresc: kolumna `fill` zajmuje wolne miejsce, inaczej
					// suma laduje pod sasiednia kolumna.
					...(col.columnDef.meta?.fill && !pinned ? { flexGrow: 1 } : {}),
					overflow: "hidden",
				}}
			>
				{dynamicSummary[col.id] ?? ""}
			</div>
		);
	};

	const allColumns = table.getAllLeafColumns();

	// Pasek sumy listy tylko dla miar addytywnych (summableColumns); przy
	// paginacji serwerowej dynamicSummary bierze wartosc z `summaryData`.
	const mobileTotalValue =
		showSummaryRow && mobileMeasureColumnId && summableColumns.includes(mobileMeasureColumnId)
			? dynamicSummary[mobileMeasureColumnId]
			: undefined;
	const mobileHasTotal = mobileTotalValue != null && mobileTotalValue !== "";

	const showToolbar =
		enableToolbar &&
		(enableColumnVisibility ||
			enableSchemes ||
			enableGrouping ||
			enableExport ||
			showAggregationOptions ||
			enableColumnColoring);

	// Desktop: w rzedzie paska narzedzi; telefon: w popoverze pod ikona Σ.
	const aggregationControls = (
		<>
			<ToolbarCheckbox
				checked={aggregationConfig.excludeNegatives}
				onChange={(v) => {
					tableBreadcrumb("aggregation.excludeNegatives", { value: v });
					setAggregationConfig((c) => ({ ...c, excludeNegatives: v }));
				}}
				label="Nie sumuj ujemnych"
			/>
			<div className="mx-0.5" />
			<ToolbarCheckbox
				checked={aggregationConfig.useAverage}
				onChange={(v) => {
					tableBreadcrumb("aggregation.useAverage", { value: v });
					setAggregationConfig((c) => ({
						...c,
						useAverage: v,
						averageExcludeZero: v ? c.averageExcludeZero : false,
						useMedian: false,
						medianExcludeZero: false,
					}));
				}}
				label="Srednia"
			/>
			<div className="mx-0.5" />
			<ToolbarCheckbox
				checked={aggregationConfig.averageExcludeZero}
				onChange={(v) => {
					tableBreadcrumb("aggregation.averageExcludeZero", { value: v });
					setAggregationConfig((c) => ({
						...c,
						averageExcludeZero: v,
						useAverage: v ? true : c.useAverage,
						useMedian: false,
						medianExcludeZero: false,
					}));
				}}
				label="Srednia bez 0"
			/>
			<div className="mx-0.5" />
			<ToolbarCheckbox
				checked={aggregationConfig.useMedian}
				onChange={(v) => {
					tableBreadcrumb("aggregation.useMedian", { value: v });
					setAggregationConfig((c) => ({
						...c,
						useMedian: v,
						medianExcludeZero: v ? c.medianExcludeZero : false,
						useAverage: v ? false : c.useAverage,
						averageExcludeZero: v ? false : c.averageExcludeZero,
					}));
				}}
				label="Mediana"
			/>
			<div className="mx-0.5" />
			<ToolbarCheckbox
				checked={aggregationConfig.medianExcludeZero}
				onChange={(v) => {
					tableBreadcrumb("aggregation.medianExcludeZero", { value: v });
					setAggregationConfig((c) => ({
						...c,
						medianExcludeZero: v,
						useMedian: v ? true : c.useMedian,
						useAverage: v ? false : c.useAverage,
						averageExcludeZero: v ? false : c.averageExcludeZero,
					}));
				}}
				label="Mediana bez 0"
			/>
		</>
	);

	return (
		<DataTableMobileContext.Provider value={mobileContextValue}>
			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				modifiers={[restrictToHorizontalAxis]}
				onDragEnd={handleDragEnd}
			>
				<div className={cn("relative flex min-h-0 flex-col", className)}>
					<div
						className={cn(
							"relative flex min-h-0 flex-1 flex-col overflow-hidden",
							isLoading && "pointer-events-none select-none blur-[2px]",
						)}
						aria-busy={isLoading}
					>
						{isMobileList ? (
							<MobileListView
								testId={tableId ? `data-table-${tableId}-mobile-list` : "data-table-mobile-list"}
								count={filteredRows.length}
								getKey={(index) => filteredRows[index]?.id ?? String(index)}
								renderRow={(index) => {
									const row = filteredRows[index];
									const value = mobileMeasureColumnId
										? toNumeric(row.getValue(mobileMeasureColumnId))
										: null;
									return {
										title: renderMobileCell(row, mobileRoles.titleId),
										subtitle: renderMobileSubtitle(row),
										measure: mobileMeasureColumnId
											? renderMobileCell(row, mobileMeasureColumnId)
											: undefined,
										bar:
											value !== null && mobileMeasureMax > 0
												? { ratio: Math.abs(value) / mobileMeasureMax, negative: value < 0 }
												: null,
									};
								}}
								onOpen={(index) => {
									const row = filteredRows[index];
									if (!row) return;
									if (onRowClick) onRowClick(row.id);
									else openMobileDetails(row.id);
								}}
								measures={mobileRoles.measureIds.map((id) => {
									const column = visibleColumns.find((c) => c.id === id);
									return { id, label: column ? mobileColumnLabel(column) : id };
								})}
								measureId={mobileMeasureColumnId}
								onMeasureChange={setMobileMeasureId}
								onOpenSort={() => setMobileSortOpen(true)}
								sortBadge={columnFilters.length}
								sortActive={sorting.length > 0}
								mode={mobileMode}
								onModeChange={setMobileMode}
								totalLabel={
									mobileHasTotal && mobileMeasureColumn
										? `${mobileAggregationLabel} · ${mobileColumnLabel(mobileMeasureColumn)} · ${mobileRowsLabel}`
										: mobileRowsLabel
								}
								totalValue={mobileHasTotal ? mobileTotalValue : undefined}
								onFetchMore={mobileInfiniteScroll ? onFetchMore : undefined}
								hasNextPage={hasNextPage}
								isFetchingNextPage={isFetchingNextPage}
							/>
						) : (
							<>
								{/* One native scroll owner keeps headers, rows and totals aligned
					    even before JavaScript receives the scroll event. */}
								<div
									ref={tableContainerRef}
									className="data-table-scroll relative min-h-0 flex-1 overflow-auto [scrollbar-gutter:stable] [container-type:inline-size]"
									style={
										{
											"--dt-header-h": `${headerHeight}px`,
											"--dt-footer-h": `${footerHeight}px`,
											"--dt-hscroll-h": `${horizontalScrollbarHeight}px`,
										} as React.CSSProperties
									}
									onScroll={handleTableScroll}
								>
									<div
										className="flex min-h-full flex-col"
										style={{ width: totalColumnsWidth, minWidth: "100%" }}
									>
										{/* Column Header */}
										<div
											ref={headerContainerRef}
											className="sticky top-0 z-40 shrink-0 border-b-2 border-border bg-muted"
										>
											<div style={{ width: totalColumnsWidth, minWidth: "100%", display: "flex" }}>
												<SortableContext
													items={sortableColumnIds}
													strategy={horizontalListSortingStrategy}
												>
													{table
														.getHeaderGroups()
														.map((headerGroup) =>
															orderedColumns.map((col) => renderHeaderCell(col, headerGroup)),
														)}
												</SortableContext>
											</div>
										</div>

										{/* Rows */}
										<div className="relative flex-1">
											<div
												style={{
													width: totalColumnsWidth,
													minWidth: "100%",
													position: "relative",
													height: shouldVirtualize ? rowVirtualizer.getTotalSize() : undefined,
												}}
											>
												{filteredRows.length === 0 ? (
													<div className="flex h-24 items-center justify-center text-sm text-muted-foreground">
														Brak danych spelniajacych kryteria filtrowania.
													</div>
												) : shouldVirtualize ? (
													rowVirtualizer
														.getVirtualItems()
														.map((vRow) => (
															<VirtualRow
																key={filteredRows[vRow.index].id}
																row={filteredRows[vRow.index]}
																index={vRow.index}
																start={vRow.start - headerHeight}
																rowHeight={rowHeight}
																orderedColumns={orderedColumns}
																columnDefs={columns}
																columnSizingKey={columnSizingKey}
																sortableColumnIds={sortableColumnIds}
																isColumnPinned={isColumnPinned}
																isLastPinnedColumn={isLastPinnedColumn}
																getLeftOffset={getLeftOffset}
																getPinnedZIndex={getPinnedZIndex}
																pinnedSeparatorClass={pinnedSeparatorClass}
																lastPinnedShadowClass={lastPinnedShadowClass}
																pinnedColumnIds={pinnedColumnIds}
																selectedRowId={selectedRowId}
																onRowClick={rowClickHandler}
																onRowCtrlClick={onRowCtrlClick}
																onRowHover={onRowHover}
																getCellColorBg={getCellColorBg}
																rowClassName={rowClassName}
															/>
														))
												) : (
													filteredRows.map((row, idx) => {
														const isSelected = row.id === selectedRowId;
														return (
															// biome-ignore lint/a11y/useSemanticElements: virtual table row — <tr> incompatible with div-based layout
															// biome-ignore lint/a11y/useKeyWithClickEvents: row selection is mouse-only by design
															// biome-ignore lint/a11y/useFocusableInteractive: intentionally non-focusable
															<div
																role="row"
																key={row.id}
																onClick={(e) => {
																	if (e.ctrlKey || e.metaKey) {
																		e.preventDefault();
																		if (onRowCtrlClick) {
																			onRowCtrlClick(row.id);
																		} else {
																			e.currentTarget
																				.querySelector<HTMLButtonElement>("button")
																				?.click();
																		}
																	} else {
																		rowClickHandler(row.id, e);
																	}
																}}
																onMouseEnter={onRowHover ? () => onRowHover(row.id) : undefined}
																style={{
																	display: "flex",
																	alignItems: "stretch",
																	minHeight: rowHeight,
																	borderBottom: "1px solid var(--color-border)",
																	background: isSelected ? "var(--color-summary-row)" : undefined,
																}}
																className={cn(
																	"group transition-colors hover:bg-muted/70 cursor-pointer",
																	!isSelected && (idx % 2 === 0 ? "bg-card" : "bg-secondary"),
																	rowClassName?.(row.original),
																)}
															>
																<SortableContext
																	items={sortableColumnIds}
																	strategy={horizontalListSortingStrategy}
																>
																	{orderedColumns.map((col) =>
																		renderDataCell(
																			col,
																			row,
																			idx % 2 === 0,
																			isSelected,
																			rowClassName?.(row.original),
																		),
																	)}
																</SortableContext>
															</div>
														);
													})
												)}
											</div>
											{/* Sentinel dla IntersectionObserver — prefetch infinite scroll */}
											<div ref={infiniteScrollSentinelRef} aria-hidden style={{ height: 1 }} />
										</div>

										<div ref={footerContainerRef} className="sticky bottom-0 z-30 shrink-0">
											{/* Infinite scroll loading indicator */}
											{isFetchingNextPage && (
												<div className="sticky left-0 flex w-[100cqw] shrink-0 items-center justify-center gap-2 border-t border-border bg-muted py-2 text-xs text-muted-foreground">
													<Loader2 className="size-3 animate-spin" />
													Ładowanie...
												</div>
											)}

											{/* Summary Row */}
											{showSummaryRow &&
												(summaryData != null ||
													summableColumns.length > 0 ||
													deriveSummary != null) && (
													<div
														data-testid="summary-row"
														className="relative grid border-t-2 border-primary/40 bg-muted"
													>
														<div style={{ gridArea: "1 / 1" }}>
															<div
																style={{
																	width: totalColumnsWidth,
																	minWidth: "100%",
																	display: "flex",
																}}
																className="text-xs font-semibold text-foreground"
															>
																{orderedColumns.map((col) => renderSummaryCell(col))}
															</div>
														</div>
														{summaryQuickView && summaryQuickView.length > 0 && (
															<div
																ref={summaryQuickViewRef}
																className="sticky right-0 z-20 flex h-full items-center justify-self-end gap-3 bg-gradient-to-l from-muted from-80% to-transparent py-1 pl-6 pr-2 text-xs transition-opacity duration-200"
																style={{ gridArea: "1 / 1" }}
															>
																{summaryQuickView.map((sq) => (
																	<span key={sq.columnId ?? sq.label}>
																		<span className="text-muted-foreground">{sq.label}:</span>{" "}
																		<span className="font-semibold tabular-nums text-foreground">
																			{sq.value ??
																				(sq.columnId ? (dynamicSummary[sq.columnId] ?? "") : "")}
																		</span>
																	</span>
																))}
															</div>
														)}
													</div>
												)}
										</div>
									</div>
								</div>
							</>
						)}
					</div>
					{isLoading && (
						<div
							className="absolute inset-0 z-[60] flex items-center justify-center rounded-md bg-background/50 backdrop-blur-[1px]"
							data-testid={
								tableId ? `data-table-${tableId}-loading-overlay` : "data-table-loading-overlay"
							}
						>
							<span className="animate-pulse text-xs font-bold uppercase tracking-widest text-muted-foreground">
								{loadingLabel}
							</span>
						</div>
					)}
					{/* Telefon w trybie „Tabela” zawsze ma pasek — to jedyne miejsce
				    przelacznika z powrotem na „Lista”. */}
					{(showToolbar || isMobileTable) && (
						<div className="z-50 shrink-0 border-t border-border bg-muted/60 px-2 py-1">
							<div className="flex items-center gap-2">
								{showToolbar ? (
									<div className="relative min-w-0 flex-1">
										<div
											ref={toolbarScrollRef}
											className="flex items-center gap-0.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
										>
											{enableSchemes && (
												<SchemeDropdown
													schemes={schemes}
													activeSchemeId={activeSchemeId}
													onApply={handleApplyScheme}
													onSave={handleSaveScheme}
													onDelete={handleDeleteScheme}
													onApplyDefault={handleApplyDefaultScheme}
												/>
											)}
											{enableColumnVisibility && (
												<ColumnToggleDropdown
													columns={allColumns as Column<TData, unknown>[]}
													columnLabels={columnLabels}
												/>
											)}
											{enableGrouping && (
												<GroupByDropdown
													columns={allColumns as Column<TData, unknown>[]}
													columnLabels={columnLabels}
													activeGroupBy={activeGroupBy}
													onGroupByChange={handleGroupByChange}
												/>
											)}
											{enableExport && (
												<ToolbarButton
													icon={
														isExportingInternal ? (
															<Loader2 className="size-3.5 animate-spin" />
														) : (
															<Download className="size-3.5" />
														)
													}
													label={isExportingInternal ? "Eksportowanie..." : "Excel"}
													onClick={handleExport}
													className={isExportingInternal ? "pointer-events-none opacity-50" : ""}
												/>
											)}
											{toolbarActions}
											{enableColumnColoring && (
												<ColumnColorDropdown
													columns={allColumns as Column<TData, unknown>[]}
													columnLabels={columnLabels}
													columnColors={columnColors}
													onColumnColorsChange={setColumnColors}
												/>
											)}
											{showAggregationOptions &&
												(isBelowMd ? (
													<Popover>
														<PopoverTrigger asChild>
															<button
																type="button"
																aria-label="Sposób sumowania"
																title="Sposób sumowania"
																className={cn(
																	"flex size-9 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
																	(aggregationConfig.excludeNegatives ||
																		aggregationConfig.useAverage ||
																		aggregationConfig.useMedian) &&
																		"bg-primary/10 text-primary",
																)}
															>
																<Sigma className="size-4" />
															</button>
														</PopoverTrigger>
														<PopoverContent
															align="start"
															className="flex w-60 flex-col items-stretch gap-0.5 p-2 [&_label]:min-h-9 [&_label]:text-sm"
														>
															{aggregationControls}
														</PopoverContent>
													</Popover>
												) : (
													<>
														{(enableSchemes ||
															enableColumnVisibility ||
															enableGrouping ||
															enableColumnColoring) && <div className="mx-1 h-4 w-px bg-border" />}
														{aggregationControls}
													</>
												))}
										</div>
										<div
											className={cn(
												"pointer-events-none absolute inset-y-0 right-0 flex w-8 items-center justify-end bg-gradient-to-l from-muted/80 to-transparent pr-0.5 transition-opacity duration-150",
												canScrollRight && !isBelowMd ? "opacity-100" : "opacity-0",
											)}
										>
											<ChevronRight className="size-3 text-muted-foreground/60" />
										</div>
									</div>
								) : (
									<div className="min-w-0 flex-1" />
								)}
								<div className="flex shrink-0 items-center gap-1">
									<button
										type="button"
										onClick={scrollToSelected}
										className={cn(
											"flex items-center gap-1 rounded bg-primary px-1.5 py-0.5 text-[10px] text-primary-foreground transition-opacity duration-200",
											showScrollTop && selectedRowId
												? "opacity-100"
												: "pointer-events-none opacity-0",
										)}
										aria-label="Przewiń do zaznaczonego"
									>
										<Crosshair className="size-3" />
										<span className="hidden sm:inline">zaznaczony</span>
									</button>
									<button
										type="button"
										onClick={scrollToTop}
										className={cn(
											"flex items-center gap-1 rounded bg-primary px-1.5 py-0.5 text-[10px] text-primary-foreground transition-opacity duration-200",
											showScrollTop ? "opacity-100" : "pointer-events-none opacity-0",
										)}
										aria-label="Przewiń do góry"
									>
										<ArrowUp className="size-3" />
										<span className="hidden sm:inline">góra</span>
									</button>
								</div>
								{isMobileTable && <TableModeToggle mode={mobileMode} onChange={setMobileMode} />}
								<div
									className={cn(
										"shrink-0 text-[10px] text-muted-foreground/60",
										isBelowMd && "min-w-0 shrink truncate",
									)}
								>
									{/* Client-side filtr + niedoladowana lista = filtr widzi tylko
								    zaladowane strony (infinite scroll wstrzymany przez safeguard).
								    Ostrzezenie do czasu retrofitu strony na serverFiltering. */}
									{clientColumnFilterActive && hasNextPage && totalCount != null && (
										<span className="mr-2 text-yellow-600 dark:text-yellow-400">
											Filtr działa tylko na załadowanych {data.length.toLocaleString("pl-PL")} z{" "}
											{totalCount.toLocaleString("pl-PL")} wierszy
										</span>
									)}
									{activeGroupBy.length > 0 ? (
										<span>
											{filteredRows.length} grup
											{serverGrouped && totalCount != null
												? ` / ${totalCount.toLocaleString("pl-PL")} wierszy`
												: ` / ${data.length} wierszy`}
											{!serverGrouped && totalCount != null && totalCount > data.length && (
												<span className="text-yellow-600 dark:text-yellow-400">
													{" "}
													(załadowano {data.length} z {totalCount.toLocaleString("pl-PL")})
												</span>
											)}
										</span>
									) : totalCount != null && totalCount !== data.length ? (
										<span>
											{data.length.toLocaleString("pl-PL")} / {totalCount.toLocaleString("pl-PL")}{" "}
											wierszy
										</span>
									) : (
										<span>
											{(serverFiltering ? data.length : filteredRows.length).toLocaleString(
												"pl-PL",
											)}{" "}
											wierszy
										</span>
									)}
								</div>
							</div>
						</div>
					)}
				</div>
			</DndContext>
			{isMobileList && (
				<MobileSortSheet
					open={mobileSortOpen}
					onOpenChange={setMobileSortOpen}
					options={visibleColumns
						.filter((c) => c.getCanSort())
						.map((c) => ({
							id: c.id,
							label: mobileColumnLabel(c),
							direction: c.getIsSorted(),
						}))}
					onSort={(id, desc) => {
						const column = visibleColumns.find((c) => c.id === id);
						if (!column) return;
						if (desc === null) column.clearSorting();
						else column.toggleSorting(desc, false);
					}}
					filters={mobileFilterableColumns.map((c) => ({
						id: c.id,
						label: mobileColumnLabel(c),
						active: c.getIsFiltered(),
						node: (
							<PendingFilterWritesContext.Provider value={pendingFilterWrites}>
								<ColumnFilterInput column={c} label={mobileColumnLabel(c)} />
							</PendingFilterWritesContext.Provider>
						),
					}))}
					onClear={() => {
						flushPendingFilterWrites(pendingFilterWrites, "discard");
						clearAllFilters();
						handleSortingChange([]);
					}}
					onPrimary={() => flushPendingFilterWrites(pendingFilterWrites, "commit")}
					resultLabel={`Pokaż ${mobileRowsLabel}`}
				/>
			)}
			{mobileFilterColumn && (
				<BottomSheet
					open
					onOpenChange={(open) => {
						if (!open) setMobileFilterColumnId(null);
					}}
					title={`Filtr: ${mobileColumnLabel(mobileFilterColumn)}`}
					data-testid="mobile-column-filter-sheet"
					footer={
						<SheetFooterActions
							onClear={() => {
								flushPendingFilterWrites(pendingFilterWrites, "discard");
								mobileFilterColumn.setFilterValue(undefined);
							}}
							clearDisabled={!mobileFilterColumn.getIsFiltered()}
							primaryLabel={`Pokaż ${mobileRowsLabel}`}
							onPrimary={() => {
								flushPendingFilterWrites(pendingFilterWrites, "commit");
								setMobileFilterColumnId(null);
							}}
						/>
					}
				>
					<div className="[&>div]:mt-0 [&>div]:h-auto [&>div]:border-t-0 [&>div]:pt-0 [&_button]:h-10 [&_input]:h-10 [&_input]:text-sm">
						<PendingFilterWritesContext.Provider value={pendingFilterWrites}>
							<ColumnFilterInput
								column={mobileFilterColumn}
								label={mobileColumnLabel(mobileFilterColumn)}
							/>
						</PendingFilterWritesContext.Provider>
					</div>
				</BottomSheet>
			)}
			{mobileDetailsRow && (
				<MobileRowDetails
					open
					onOpenChange={(open) => {
						if (!open) setMobileDetailsRowId(null);
					}}
					title={renderMobileCell(mobileDetailsRow, mobileRoles.titleId) ?? "Szczegóły wiersza"}
					titleText={
						mobileRoles.titleId
							? String(mobileDetailsRow.getValue(mobileRoles.titleId) ?? "")
							: undefined
					}
					subtitle={renderMobileSubtitle(mobileDetailsRow)}
					kpis={mobileRoles.measureIds.slice(0, 3).flatMap((id) => {
						const column = visibleColumns.find((c) => c.id === id);
						return column
							? [
									{
										id,
										label: mobileColumnLabel(column),
										value: renderMobileCell(mobileDetailsRow, id),
										numeric: true,
									},
								]
							: [];
					})}
					fields={mobileRoles.detailIds.flatMap((id) => {
						const column = visibleColumns.find((c) => c.id === id);
						return column
							? [
									{
										id,
										label: mobileColumnLabel(column),
										value: renderMobileCell(mobileDetailsRow, id),
										numeric: !!column.columnDef.meta?.isNumeric,
										help: mobileColumnHelp(column),
									},
								]
							: [];
					})}
					actions={
						mobileRoles.actionIds.length > 0 || onRowCtrlClick || onRowSelect ? (
							<>
								{mobileRoles.actionIds.map((id) => (
									<div key={id} className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
										{renderMobileCell(mobileDetailsRow, id)}
									</div>
								))}
								{(onRowCtrlClick || onRowSelect) && (
									<button
										type="button"
										data-testid="mobile-row-open"
										onClick={() => {
											setMobileDetailsRowId(null);
											if (onRowCtrlClick) onRowCtrlClick(mobileDetailsRow.id);
											else onRowSelect?.(mobileDetailsRow.original);
										}}
										className="h-11 flex-1 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
									>
										{mobileOpenLabel}
									</button>
								)}
							</>
						) : undefined
					}
				/>
			)}
		</DataTableMobileContext.Provider>
	);
}

// Typy kolumn z generykiem TFeatures zwiazanym z DataTable (v9) — strony
// importuja je stad zamiast z `@tanstack/react-table`.
export type { Column, ColumnDef, ColumnFiltersState, Row, RowData, SortingState, VisibilityState };
